/**
 * バックアップ作成日時: 2025-11-02 00:26
 *
 * このバックアップの状態:
 * - テキスト消失問題: 修正済み（長方形・楕円のテキストが正しく復元される）
 * - 直線の移動問題: 未解決（getBoundingRect()が常に(0,0)を返す問題を特定）
 * - キャンバス管理: Phase 1-2の改善完了（transformState管理、キャンバス再作成の防止）
 * - 座標強制復元: 実装済みだが効果なし
 *
 * 次の実装予定:
 * - 直線をグループオブジェクトとして扱う（長方形・楕円と同じ処理）
 */
import { fabric } from "fabric";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import "./DrawLayer.css";

// グローバルな保留オブジェクト管理
const globalPendingObjects = new Map();
// グローバルな最後作成オブジェクトID管理
let globalLastCreatedObjectId = null;

// Path直線用のカスタムコントロール設定関数
const setupLinePathControls = (linePath) => {
  // 始点のコントロール
  const startPointControl = new fabric.Control({
    name: "startPoint",
    positionHandler: function (dim, finalMatrix, fabricObject) {
      // pathから始点座標を取得（pathコマンドは既に絶対座標）
      const pathCommands = fabricObject.path;
      if (pathCommands && pathCommands.length >= 1) {
        const x = pathCommands[0][1];
        const y = pathCommands[0][2];

        console.log(
          "[LINE_CONTROL_DEBUG] Start point: " +
            JSON.stringify(
              {
                x: x,
                y: y,
                lineId: fabricObject.id,
              },
              null,
              2
            )
        );

        // viewportTransformのみを適用
        return fabric.util.transformPoint(
          { x: x, y: y },
          fabricObject.canvas.viewportTransform
        );
      }
      return { x: 0, y: 0 };
    },
    actionHandler: function (eventData, transform, x, y) {
      const target = transform.target;
      const pointer = target.canvas.getPointer(eventData.e);
      const pathCommands = target.path;

      if (pathCommands && pathCommands.length >= 2) {
        // 終点座標を保持（pathは既に絶対座標）
        const x2 = pathCommands[1][1];
        const y2 = pathCommands[1][2];

        // 新しいpathを作成（始点のみ更新）
        const newPathData = `M ${pointer.x} ${pointer.y} L ${x2} ${y2}`;
        target.set("path", fabric.util.parsePath(newPathData));
        // left/topを強制的に0に設定（Fabric.jsが自動設定するのを防ぐ）
        target.set({ left: 0, top: 0 });
        target.setCoords();

        console.log(
          "[LINE_CONTROL_DEBUG] Start point moved: " +
            JSON.stringify(
              {
                newStart: { x: pointer.x, y: pointer.y },
                endKept: { x: x2, y: y2 },
              },
              null,
              2
            )
        );
      }

      return true;
    },
    render: function (ctx, left, top, styleOverride, fabricObject) {
      ctx.save();
      ctx.fillStyle = "rgba(35, 151, 206, 0.8)";
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(left, top, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    },
    cursorStyle: "move",
  });

  // 終点のコントロール
  const endPointControl = new fabric.Control({
    name: "endPoint",
    positionHandler: function (dim, finalMatrix, fabricObject) {
      // pathから終点座標を取得（pathコマンドは既に絶対座標）
      const pathCommands = fabricObject.path;
      if (pathCommands && pathCommands.length >= 2) {
        const x = pathCommands[1][1];
        const y = pathCommands[1][2];

        console.log(
          "[LINE_CONTROL_DEBUG] End point: " +
            JSON.stringify(
              {
                x: x,
                y: y,
                lineId: fabricObject.id,
              },
              null,
              2
            )
        );

        // viewportTransformのみを適用
        return fabric.util.transformPoint(
          { x: x, y: y },
          fabricObject.canvas.viewportTransform
        );
      }
      return { x: 0, y: 0 };
    },
    actionHandler: function (eventData, transform, x, y) {
      const target = transform.target;
      const pointer = target.canvas.getPointer(eventData.e);
      const pathCommands = target.path;

      if (pathCommands && pathCommands.length >= 2) {
        // 始点座標を保持（pathは既に絶対座標）
        const x1 = pathCommands[0][1];
        const y1 = pathCommands[0][2];

        // 新しいpathを作成（終点のみ更新）
        const newPathData = `M ${x1} ${y1} L ${pointer.x} ${pointer.y}`;
        target.set("path", fabric.util.parsePath(newPathData));
        // left/topを強制的に0に設定（Fabric.jsが自動設定するのを防ぐ）
        target.set({ left: 0, top: 0 });
        target.setCoords();

        console.log(
          "[LINE_CONTROL_DEBUG] End point moved: " +
            JSON.stringify(
              {
                startKept: { x: x1, y: y1 },
                newEnd: { x: pointer.x, y: pointer.y },
              },
              null,
              2
            )
        );
      }

      return true;
    },
    render: function (ctx, left, top, styleOverride, fabricObject) {
      ctx.save();
      ctx.fillStyle = "rgba(35, 151, 206, 0.8)";
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(left, top, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    },
    cursorStyle: "move",
  });

  // コントロールを設定（デフォルトのコントロールを無効化して始点・終点のみ表示）
  linePath.controls = {
    startPoint: startPointControl,
    endPoint: endPointControl,
  };

  // デフォルトのコントロールを無効化
  linePath.setControlsVisibility({
    mt: false,
    mb: false,
    ml: false,
    mr: false,
    bl: false,
    br: false,
    tl: false,
    tr: false,
    mtr: false,
  });

  console.log(
    "[LINE_CONTROL] Custom controls set up for line path: " +
      JSON.stringify(
        {
          lineId: linePath.id,
          hasControls: !!linePath.controls,
          controlNames: Object.keys(linePath.controls || {}),
        },
        null,
        2
      )
  );
};

const DrawLayer = ({
  drawData = [],
  onDrawDataChange,
  editable = false,
  width = "100%", // 動的サイズ対応
  height = "100%", // 動的サイズ対応
  isDrawingMode = false,
  drawingTool = "rectangle",
  showStylePanel = false,
  onStylePanelClose,
  onObjectCreated,
  onObjectSelected,
  onObjectDeselected,
  transformState,
  clearDrawSelectionRef,
  isPrintMode = false, // 印刷モードフラグ
}) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [actualSize, setActualSize] = useState({ width: 1122, height: 794 });
  const [canvasInitialized, setCanvasInitialized] = useState(false); // キャンバス初期化フラグ
  const [currentStyle, setCurrentStyle] = useState({
    strokeWidth: 2,
    stroke: "#000000",
    fill: "transparent",
    opacity: 1, // 互換性のための全体透明度（非推奨）
    fillOpacity: 1, // 中身の色の透明度
    strokeOpacity: 1, // 枠線の透明度
    text: "",
    textColor: "#000000",
    fontSize: 16,
    fontWeight: 400,
    textAlignH: "center",
    textAlignV: "middle",
    angle: 0, // 回転角度を追加
    cornerRadius: 0, // 角の丸みを追加
    shapeType: "rectangle", // 選択されているオブジェクトのタイプ
  });

  // リサイズ完了後の再選択管理用state（削除）
  // const [resizeCompleted, setResizeCompleted] = useState(null);

  // 描画状態をrefで管理（再レンダリングで失われないように）
  const drawingStateRef = useRef({
    isDrawing: false,
    startPoint: null,
    drawingObject: null,
    sizeUpdatePaused: false, // サイズ更新の一時停止フラグ
    recentlyCompleted: false, // 描画完了直後フラグ
    completionTimestamp: null, // 描画完了時刻
  });

  // 選択状態をrefで管理（データ更新による再レンダリングで失われないように）
  const selectionStateRef = useRef({
    selectedObjectId: null,
    isUpdatingData: false,
    lineScaleResetProcessed: {}, // 直線のscaleリセット処理済みフラグ
    lineSavedCoordinates: {}, // 直線の保存済み座標（移動後の正しい位置）
  });

  // リトライ回数を管理するRef
  const retryCountRef = useRef(0);
  const maxRetries = 10; // 最大10回まで

  // 描画データをキャンバスに読み込む（useCallbackでメモ化）
  const loadDrawData = useCallback(
    (canvas, data) => {
      const callStack = new Error().stack;
      console.log("[LINE_DEBUG] loadDrawData called", {
        dataLength: data.length,
        timestamp: Date.now(),
        calledFrom: callStack.split("\n")[2]?.trim(),
      });

      // グローバルな保留オブジェクトもチェック
      const globalPendingObjectIds = Array.from(
        globalPendingObjects.values()
      ).map((obj) => obj.objectId);

      // 現在選択されているオブジェクトを保存
      const activeObject = canvas.getActiveObject();
      const activeObjectId = activeObject?.id;

      // 既存のオブジェクトのIDとオブジェクト自体を保存
      const existingObjects = canvas.getObjects();
      const existingIds = existingObjects
        .map((obj) => obj.id)
        .filter((id) => id);

      console.log("[LINE_DEBUG] Existing IDs in canvas:", {
        existingIds: existingIds,
        existingCount: existingIds.length,
        totalObjects: existingObjects.length,
      });

      // 新しいデータのIDを取得（正規化版）
      const newIds = data.map((item) => {
        const normalizedItem = normalizeDrawData(item);
        return normalizedItem.id;
      });

      // 保留中のオブジェクトがある場合は保護（ローカルとグローバル両方）
      const pendingObjectId =
        selectionStateRef.current.pendingNewObject?.objectId;

      // 削除されたオブジェクトを除去（保留中のオブジェクトのみ保護）
      canvas.getObjects().forEach((obj) => {
        if (
          obj.id &&
          !newIds.includes(obj.id) &&
          obj.id !== pendingObjectId &&
          !globalPendingObjectIds.includes(obj.id)
        ) {
          console.log("[LINE_DEBUG] Removing object not in new data", {
            id: obj.id,
            type: obj.type,
            isLinePath: obj.isLinePath,
          });
          canvas.remove(obj);
        }
      });

      // 既存オブジェクトのスタイルを更新
      data.forEach((item) => {
        // データ形式を正規化（新旧両方に対応）
        const normalizedItem = normalizeDrawData(item);

        // 既存オブジェクトで更新中の場合は、削除も作成もスキップ
        if (existingIds.includes(normalizedItem.id)) {
          // 選択中で更新中のオブジェクトは何もしない（すべてのタイプ）
          if (
            selectionStateRef.current.isUpdatingData &&
            selectionStateRef.current.selectedObjectId === normalizedItem.id
          ) {
            return; // このオブジェクトは完全にスキップ（削除も作成もしない）
          }

          // 通常の場合は削除して再作成（直線も含むすべてのタイプ）
          const existingObj = canvas
            .getObjects()
            .find((obj) => obj.id === normalizedItem.id);
          if (existingObj) {
            console.log(
              "[LINE_DEBUG] Removing existing object for recreation",
              {
                id: normalizedItem.id,
                type: normalizedItem.type,
              }
            );
            canvas.remove(existingObj);
          }
          // returnせずに新規作成に進む
        }

        // 新しいオブジェクトを作成
        let fabricObject;

        switch (normalizedItem.type) {
          case "ellipse":
            // 相対座標から絶対座標に変換
            const ellipseCanvasWidth = canvas.width;
            const ellipseCanvasHeight = canvas.height;
            const ellipseAbsoluteLeft =
              (normalizedItem.x / 100) * ellipseCanvasWidth;
            const ellipseAbsoluteTop =
              (normalizedItem.y / 100) * ellipseCanvasHeight;
            const ellipseAbsoluteWidth =
              (normalizedItem.width / 100) * ellipseCanvasWidth;
            const ellipseAbsoluteHeight =
              (normalizedItem.height / 100) * ellipseCanvasHeight;

            const ellipse = new fabric.Ellipse({
              left: ellipseAbsoluteLeft + ellipseAbsoluteWidth / 2,
              top: ellipseAbsoluteTop + ellipseAbsoluteHeight / 2,
              rx: ellipseAbsoluteWidth / 2,
              ry: ellipseAbsoluteHeight / 2,
              originX: "center",
              originY: "center",
              fill: normalizedItem.fill,
              stroke: normalizedItem.stroke,
              strokeWidth: normalizedItem.strokeWidth,
              strokeUniform: true,
              strokeDashArray: null,
              strokeLineCap: "butt",
              strokeLineJoin: "miter",
              strokeMiterLimit: 4,
              paintFirst: "fill",
              opacity: normalizedItem.opacity,
              angle: 0, // 楕円自体は回転させない（グループで回転）
              selectable: editable,
              evented: editable,
              lockScalingFlip: true, // リサイズ時の反転を防止
            });

            // テキストが設定されている場合はテキストも追加
            if (normalizedItem.text && normalizedItem.text.trim()) {
              // フォントサイズを相対値から絶対値に変換
              let ellipseAbsoluteFontSize = normalizedItem.fontSize;
              if (isFontSizeRelative(normalizedItem.fontSize)) {
                ellipseAbsoluteFontSize = convertFontSizeToAbsolute(
                  normalizedItem.fontSize,
                  ellipseCanvasHeight
                );
              }

              // テキストの配置を計算（楕円の場合も長方形と同じ計算）
              const ellipseTextLeft =
                ellipseAbsoluteLeft +
                calculateTextLeftForGroup(
                  ellipseAbsoluteWidth,
                  normalizedItem.textAlignH
                );
              const ellipseTextTop =
                ellipseAbsoluteTop +
                calculateTextTopForGroup(
                  ellipseAbsoluteHeight,
                  normalizedItem.textAlignV
                );

              const ellipseText = new fabric.Text(normalizedItem.text, {
                left: ellipseTextLeft,
                top: ellipseTextTop,
                fontSize: ellipseAbsoluteFontSize,
                fill: normalizedItem.textColor,
                fontWeight: normalizedItem.fontWeight,
                textAlign: normalizedItem.textAlignH,
                originX: getOriginX(normalizedItem.textAlignH),
                originY: getOriginY(normalizedItem.textAlignV),
                selectable: false,
                evented: false,
              });

              // 楕円とテキストをグループ化（左上座標を中心座標に変換）
              fabricObject = new fabric.Group([ellipse, ellipseText], {
                left: ellipseAbsoluteLeft + ellipseAbsoluteWidth / 2, // 中心X座標
                top: ellipseAbsoluteTop + ellipseAbsoluteHeight / 2, // 中心Y座標
                angle: normalizedItem.angle,
                selectable: editable,
                evented: editable,
              });

              // console.log('[GROUP_POSITION_DEBUG] Loading ellipse group from data:', ...);
            } else {
              // 単一楕円の場合、データから角度を復元
              ellipse.set("angle", normalizedItem.angle);
              fabricObject = ellipse;
            }
            break;
          case "line":
            // 直線の読み込み（Pathオブジェクトで実装）
            const lineCanvasWidth = canvas.width;
            const lineCanvasHeight = canvas.height;
            const x1Absolute = (normalizedItem.x1 / 100) * lineCanvasWidth;
            const y1Absolute = (normalizedItem.y1 / 100) * lineCanvasHeight;
            const x2Absolute = (normalizedItem.x2 / 100) * lineCanvasWidth;
            const y2Absolute = (normalizedItem.y2 / 100) * lineCanvasHeight;

            // PathオブジェクトでLineを表現
            // 選択判定用に太いstrokeWidthを設定
            const selectionStrokeWidth = Math.max(
              normalizedItem.strokeWidth,
              20
            );
            const pathData = `M ${x1Absolute} ${y1Absolute} L ${x2Absolute} ${y2Absolute}`;
            const linePath = new fabric.Path(pathData, {
              fill: null,
              stroke: normalizedItem.stroke,
              strokeWidth: selectionStrokeWidth, // 内部的に太い線として扱う
              strokeUniform: true,
              strokeLineCap: "round",
              strokeLineJoin: "round",
              opacity: normalizedItem.opacity,
              selectable: editable,
              evented: editable,
              objectCaching: false,
              perPixelTargetFind: true, // ピクセル判定を有効化
              hasRotatingPoint: false,
              lockRotation: true,
              lockMovementX: true, // X軸移動を無効化
              lockMovementY: true, // Y軸移動を無効化
              padding: 10, // 選択範囲を広げる
              hoverCursor: "move", // ホバー時のカーソルを変更
            });

            // カスタムプロパティでLineとして識別
            linePath.set("isLinePath", true);
            linePath.set("_originalStrokeWidth", normalizedItem.strokeWidth); // 元の太さを保存

            // カスタムレンダリング：実際は細い線として描画
            linePath._render = function (ctx) {
              const savedStrokeWidth = this.strokeWidth;
              this.strokeWidth = this._originalStrokeWidth || savedStrokeWidth;
              fabric.Path.prototype._render.call(this, ctx);
              this.strokeWidth = savedStrokeWidth;
            };

            // カスタムコントロール（始点・終点）を設定
            setupLinePathControls(linePath);

            fabricObject = linePath;

            console.log(
              "[PATH_LINE] Line loaded as Path object: " +
                JSON.stringify(
                  {
                    lineId: normalizedItem.id,
                    pathData: pathData,
                    bounds: linePath.getBoundingRect(),
                    coordinates: {
                      x1: x1Absolute,
                      y1: y1Absolute,
                      x2: x2Absolute,
                      y2: y2Absolute,
                    },
                    padding: 10,
                  },
                  null,
                  2
                )
            );
            break;
          case "rectangle":
            // 相対座標から絶対座標に変換（canvasの実際のサイズを使用）
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const absoluteLeft = (normalizedItem.x / 100) * canvasWidth;
            const absoluteTop = (normalizedItem.y / 100) * canvasHeight;
            const absoluteWidth = (normalizedItem.width / 100) * canvasWidth;
            const absoluteHeight = (normalizedItem.height / 100) * canvasHeight;

            const rect = new fabric.Rect({
              left: absoluteLeft,
              top: absoluteTop,
              width: absoluteWidth,
              height: absoluteHeight,
              fill: normalizedItem.fill,
              stroke: normalizedItem.stroke,
              strokeWidth: normalizedItem.strokeWidth,
              strokeUniform: true, // 枠線の太さを一定に保つ
              strokeDashArray: null,
              strokeLineCap: "butt",
              strokeLineJoin: "miter",
              strokeMiterLimit: 4,
              paintFirst: "fill", // 塗りつぶしを先に描画
              opacity: normalizedItem.opacity,
              rx: normalizedItem.cornerRadius || 0, // 角の丸みを適用
              ry: normalizedItem.cornerRadius || 0, // 角の丸みを適用
              angle: 0, // 長方形自体は回転させない（グループで回転）
              selectable: editable,
              evented: editable,
            });

            // テキストが設定されている場合はテキストも追加
            if (normalizedItem.text && normalizedItem.text.trim()) {
              // フォントサイズを相対値から絶対値に変換
              let absoluteFontSize = normalizedItem.fontSize;
              if (isFontSizeRelative(normalizedItem.fontSize)) {
                absoluteFontSize = convertFontSizeToAbsolute(
                  normalizedItem.fontSize,
                  canvasHeight
                );
              }

              // テキストの配置を計算
              const textLeft = calculateTextLeft(
                rect,
                normalizedItem.textAlignH
              );
              const textTop = calculateTextTop(rect, normalizedItem.textAlignV);

              const text = new fabric.Text(normalizedItem.text, {
                left: textLeft,
                top: textTop,
                fontSize: absoluteFontSize,
                fill: normalizedItem.textColor,
                fontWeight: normalizedItem.fontWeight,
                textAlign: normalizedItem.textAlignH,
                originX: getOriginX(normalizedItem.textAlignH),
                originY: getOriginY(normalizedItem.textAlignV),
                selectable: false,
                evented: false,
              });

              // 長方形とテキストをグループ化
              fabricObject = new fabric.Group([rect, text], {
                left: rect.left,
                top: rect.top,
                angle: normalizedItem.angle, // グループ全体に回転角度を適用
                selectable: editable,
                evented: editable,
              });
            } else {
              // 単一長方形の場合のみ、データから角度を復元
              rect.set("angle", normalizedItem.angle);
              fabricObject = rect;
            }
            break;
          default:
            return;
        }

        if (fabricObject) {
          fabricObject.set("id", normalizedItem.id);
          canvas.add(fabricObject);
        }
      });

      canvas.renderAll();

      // 選択状態を復元（通常の場合）
      if (activeObjectId) {
        const objectToSelect = canvas
          .getObjects()
          .find((obj) => obj.id === activeObjectId);
        if (objectToSelect) {
          canvas.setActiveObject(objectToSelect);
          console.log(
            "DrawLayer: Selection restored after data update",
            activeObjectId
          );
        }
      }

      // データ更新による再レンダリング後の選択状態復元
      if (
        selectionStateRef.current.isUpdatingData &&
        selectionStateRef.current.selectedObjectId
      ) {
        const objectToReselect = canvas
          .getObjects()
          .find((obj) => obj.id === selectionStateRef.current.selectedObjectId);
        if (objectToReselect) {
          canvas.setActiveObject(objectToReselect);
          canvas.renderAll();
          console.log(
            "DrawLayer: Selection restored after style update",
            selectionStateRef.current.selectedObjectId
          );
        }

        // 状態はクリアせずに維持（選択解除時にクリア）
      }

      // 最後に作成されたオブジェクトを選択（グローバル変数もチェック）
      const lastCreatedObjectId =
        selectionStateRef.current.lastCreatedObjectId ||
        globalLastCreatedObjectId;

      if (lastCreatedObjectId) {
        selectionStateRef.current.lastCreatedObjectId = null;
        globalLastCreatedObjectId = null;

        const lastCreatedObject = canvas
          .getObjects()
          .find((obj) => obj.id === lastCreatedObjectId);

        if (lastCreatedObject) {
          canvas.setActiveObject(lastCreatedObject);
          canvas.renderAll();
          console.log(
            "DrawLayer: Last created object selected after data load",
            lastCreatedObjectId
          );

          // オブジェクト選択を通知（安全に実行）
          if (onObjectSelected) {
            onObjectSelected(lastCreatedObjectId);
          }
        } else {
          console.log("DrawLayer: Last created object not found on canvas");
        }
      }
    },
    [editable]
  );

  // canvas作成の共通関数
  const createCanvasWithSize = useCallback(
    (canvasSize) => {
      // 既存のキャンバスがある場合は何もしない
      if (fabricCanvasRef.current && !fabricCanvasRef.current._disposed) {
        console.log("DrawLayer: Canvas already exists, skipping creation");
        return;
      }

      console.log(
        "[DRAW_CANVAS:INFO] Creating Fabric.js canvas with provided size",
        canvasSize
      );

      const canvas = new fabric.Canvas(canvasRef.current, {
        width: canvasSize.width,
        height: canvasSize.height,
        backgroundColor: "transparent",
        selection: editable,
        preserveObjectStacking: true,
        // レイアウトズレ防止のための厳密な設定
        allowTouchScrolling: false,
        imageSmoothingEnabled: false,
        enableRetinaScaling: false,
        renderOnAddRemove: true,
        skipTargetFind: false,
        // 選択時のコントロールを最小限に
        selectionBorderColor: "rgba(35, 151, 206, 0.8)",
        selectionLineWidth: 1,
        selectionDashArray: [5, 5],
        // ホバー効果を無効化
        hoverCursor: "default",
        moveCursor: "default",
        // レイアウトに影響しないように
        absolutePositioned: true,
      });

      console.log(
        "DrawLayer: Fabric.js canvas created successfully (fallback)",
        {
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          canvasSelection: canvas.selection,
        }
      );

      fabricCanvasRef.current = canvas;
      setActualSize(canvasSize);
      setCanvasInitialized(true); // フラグを立てる

      // 初期化時はloadDrawDataを呼ばない（drawData監視useEffectで処理）
      console.log(
        "[LINE_DEBUG] Canvas created in fallback, drawData will be loaded by separate useEffect",
        {
          canvasSize: { width: canvas.width, height: canvas.height },
          timestamp: Date.now(),
        }
      );
    },
    [editable]
  );

  // PageEditorのsection要素のtransform適用を監視してサイズを取得
  const getAccurateSize = useCallback(() => {
    const element = containerRef.current;
    if (!element) {
      console.log("DrawLayer: No element, using default size");
      return { width: 1122, height: 794 };
    }

    // PageEditor内のsection要素のサイズを優先的に使用（より具体的に取得）
    // 印刷モードと編集モード両方に対応
    const sectionElement =
      document.querySelector(".right-side-area section.editing.print_pages") ||
      document.querySelector("section.print_pages") || // 印刷ビュー用
      containerRef.current?.closest(".page-editor")?.querySelector("section") ||
      document.querySelector(".page-editor section") ||
      document.querySelector("section");
    if (sectionElement) {
      const computedStyle = window.getComputedStyle(sectionElement);
      const transform =
        sectionElement.style.transform || computedStyle.transform;

      // transformが適用されている場合は変換後のサイズを取得
      if (transform && transform !== "none" && transform.includes("scale(")) {
        const sectionRect = sectionElement.getBoundingClientRect();
        console.log("DrawLayer: Using section transformed size", {
          transform: transform,
          sectionSize: { width: sectionRect.width, height: sectionRect.height },
          sectionElement: {
            tagName: sectionElement.tagName,
            className: sectionElement.className,
          },
        });
        return { width: sectionRect.width, height: sectionRect.height };
      } else {
        console.log("DrawLayer: section found but no scale transform yet", {
          transform: transform,
          willWaitForTransform: true,
        });
      }
    }

    // フォールバック：DrawLayerコンテナサイズを使用
    const elementRect = element.getBoundingClientRect();
    console.log("DrawLayer: Using DrawLayer container size (fallback)", {
      containerSize: { width: elementRect.width, height: elementRect.height },
      reason: sectionElement ? "No transform yet" : "No section found",
    });

    return { width: elementRect.width, height: elementRect.height };

    // フォールバック：親要素のサイズを取得
    const parentElement = element.parentElement;
    if (parentElement) {
      const parentRect = parentElement.getBoundingClientRect();
      console.log("DrawLayer: Using parent element size (fallback)", {
        parentSize: { width: parentRect.width, height: parentRect.height },
        elementSize: {
          width: element.offsetWidth,
          height: element.offsetHeight,
        },
        parentElement: {
          tagName: parentElement.tagName,
          className: parentElement.className,
          id: parentElement.id,
        },
      });
      return { width: parentRect.width, height: parentRect.height };
    }

    // フォールバック：要素自体のサイズ
    const rect = element.getBoundingClientRect();
    console.log("DrawLayer: Using element size as fallback", {
      elementSize: { width: rect.width, height: rect.height },
    });
    return { width: rect.width, height: rect.height };
  }, []);

  // transformStateを使用したサイズ管理（フォールバック付き）
  useEffect(() => {
    if (!transformState) {
      console.log(
        "DrawLayer: No transformState provided, using fallback size detection"
      );
      // フォールバック：コンテナサイズを使用
      if (containerRef.current) {
        const elementRect = containerRef.current.getBoundingClientRect();
        const fallbackSize = {
          width: elementRect.width,
          height: elementRect.height,
        };
        console.log("DrawLayer: Using fallback container size", {
          previousSize: actualSize,
          fallbackSize: fallbackSize,
        });
        setActualSize(fallbackSize);
      }
      return;
    }

    console.log("DrawLayer: transformState updated", {
      isTransformApplied: transformState.isTransformApplied,
      transformedSize: transformState.transformedSize,
      scale: transformState.scale,
      timestamp: transformState.timestamp,
    });

    if (!transformState.isTransformApplied) {
      console.log("DrawLayer: Transform not applied yet, waiting...");
      return;
    }

    if (
      !transformState.transformedSize ||
      !transformState.transformedSize.width ||
      !transformState.transformedSize.height
    ) {
      console.log(
        "DrawLayer: Invalid transformedSize, using fallback",
        transformState.transformedSize
      );
      // フォールバック：コンテナサイズを使用
      if (containerRef.current) {
        const elementRect = containerRef.current.getBoundingClientRect();
        const fallbackSize = {
          width: elementRect.width,
          height: elementRect.height,
        };
        setActualSize(fallbackSize);
      }
      return;
    }

    // 正常な場合のみサイズを設定
    console.log("DrawLayer: Using transformState size (VERIFIED)", {
      previousSize: actualSize,
      newSize: transformState.transformedSize,
      scale: transformState.scale,
      verified: true,
    });
    setActualSize(transformState.transformedSize);
  }, [transformState]);

  // section要素のサイズ変更を監視
  useEffect(() => {
    if (!containerRef.current) return;

    // PageEditorからの強制リサイズ関数を登録（互換性のため）
    const forceResize = () => {
      if (transformState && transformState.isTransformApplied) {
        console.log(
          "DrawLayer: Force resize using transformState",
          transformState.transformedSize
        );
        setActualSize(transformState.transformedSize);
      } else {
        const elementRect = containerRef.current.getBoundingClientRect();
        const newSize = {
          width: elementRect.width,
          height: elementRect.height,
        };
        console.log("DrawLayer: Force resize using container size", newSize);
        setActualSize(newSize);
      }
    };

    // グローバルに登録してPageEditorから呼び出し可能にする
    window.drawLayerForceResize = forceResize;

    // section要素のサイズ変更を監視
    const sectionElement = document.querySelector(
      ".right-side-area section.editing.print_pages"
    );
    console.log("[DRAW_SECTION:CRITICAL] Section element search result", {
      found: !!sectionElement,
      size: sectionElement
        ? {
            width: sectionElement.getBoundingClientRect().width,
            height: sectionElement.getBoundingClientRect().height,
          }
        : null,
      allSections: Array.from(document.querySelectorAll("section")).map(
        (s) => ({
          className: s.className,
          size: {
            width: s.getBoundingClientRect().width,
            height: s.getBoundingClientRect().height,
          },
        })
      ),
    });

    if (sectionElement) {
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          // 描画中または描画完了直後はサイズ更新を一時停止
          if (
            drawingStateRef.current.isDrawing ||
            drawingStateRef.current.sizeUpdatePaused ||
            drawingStateRef.current.recentlyCompleted
          ) {
            console.log(
              "[DRAW_RESIZE:INFO] Size update paused during/after drawing",
              {
                isDrawing: drawingStateRef.current.isDrawing,
                sizeUpdatePaused: drawingStateRef.current.sizeUpdatePaused,
                recentlyCompleted: drawingStateRef.current.recentlyCompleted,
                completionTimestamp:
                  drawingStateRef.current.completionTimestamp,
                timeSinceCompletion: drawingStateRef.current.completionTimestamp
                  ? Date.now() - drawingStateRef.current.completionTimestamp
                  : null,
                timestamp: Date.now(),
              }
            );
            return;
          }

          const newSize = {
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          };

          console.log(
            "[DRAW_RESIZE:CRITICAL] Section size changed, updating actualSize",
            {
              previousSize: actualSize,
              newSize: newSize,
              timestamp: Date.now(),
            }
          );

          setActualSize(newSize);
        }
      });

      resizeObserver.observe(sectionElement);
      console.log(
        "[DRAW_RESIZE:INFO] ResizeObserver attached to section element"
      );

      return () => {
        resizeObserver.disconnect();
        delete window.drawLayerForceResize;
        console.log("[DRAW_RESIZE:INFO] ResizeObserver disconnected");
      };
    }

    return () => {
      delete window.drawLayerForceResize;
    };
  }, [transformState]);

  // サイズ変更の監視を完全に無効化（PageEditorに委譲）
  // useEffect(() => {
  //   // この処理を無効化してPageEditorとの競合を完全に回避
  // }, [width, height]);

  // Fabric.jsキャンバスの初期化（transformState準備完了後）
  useEffect(() => {
    const debugInfo = {
      hasCanvasRef: !!canvasRef.current,
      actualSize,
      editable,
      isPrintMode,
      drawDataLength: drawData.length,
      hasExistingCanvas: !!fabricCanvasRef.current,
      canvasInitialized,
      hasTransformState: !!transformState,
      isTransformApplied: transformState?.isTransformApplied,
      transformStateTimestamp: transformState?.timestamp,
    };

    console.log("DrawLayer: Canvas initialization useEffect called", debugInfo);

    // 印刷モード専用のデバッグログ
    if (isPrintMode) {
      console.log("[PRINT_VIEW_DEBUG] DrawLayer in print mode:", {
        ...debugInfo,
        drawData: drawData,
        willInitialize:
          canvasRef.current && (editable || isPrintMode) && !canvasInitialized,
      });
    }

    // 印刷モードまたは編集モードの場合のみキャンバスを初期化
    if (!canvasRef.current || (!editable && !isPrintMode)) {
      console.log("DrawLayer: Canvas initialization skipped", {
        hasCanvasRef: !!canvasRef.current,
        editable,
        isPrintMode,
        reason: !canvasRef.current
          ? "No canvas ref"
          : "Not editable and not print mode",
      });
      return;
    }

    // 印刷モードでdrawDataが空の場合はスキップ
    if (isPrintMode && drawData.length === 0) {
      console.log(
        "DrawLayer: Canvas initialization skipped in print mode (no drawData)",
        {
          isPrintMode,
          drawDataLength: drawData.length,
        }
      );
      return;
    }

    // キャンバスが既に初期化済みの場合はスキップ（最優先チェック）
    if (canvasInitialized) {
      console.log(
        "DrawLayer: Canvas already initialized, skipping recreation",
        {
          canvasInitialized: true,
          hasCanvas: !!fabricCanvasRef.current,
        }
      );
      return;
    }

    // transformStateが利用可能になるまで待機（印刷モードを除く）
    if (
      !isPrintMode &&
      (!transformState || !transformState.isTransformApplied)
    ) {
      console.log(
        "DrawLayer: Waiting for transformState to be applied before canvas creation"
      );
      return;
    }

    // 印刷モードでtransformStateがない場合は、section要素から直接サイズを取得
    if (isPrintMode && !transformState) {
      console.log(
        "[PRINT_VIEW_DEBUG] Print mode without transformState, will use section size directly"
      );
    }

    // 既存のキャンバスがある場合は何もしない
    if (fabricCanvasRef.current && !fabricCanvasRef.current._disposed) {
      console.log("DrawLayer: Canvas already exists, skipping creation", {
        canvasExists: true,
        canvasWidth: fabricCanvasRef.current.width,
        canvasHeight: fabricCanvasRef.current.height,
        objectCount: fabricCanvasRef.current.getObjects().length,
      });
      setCanvasInitialized(true); // 既存キャンバスがある場合もフラグを立てる
      return;
    }

    // 実際のsection要素のサイズを取得してcanvasを作成
    // 印刷モード用のsection要素も検索
    const sectionElement =
      document.querySelector(".right-side-area section.editing.print_pages") ||
      document.querySelector("section.print_pages");

    let canvasSize;

    // 印刷モードでtransformStateがない場合は、section要素から直接取得
    if (isPrintMode && !transformState) {
      if (sectionElement) {
        const sectionRect = sectionElement.getBoundingClientRect();
        canvasSize = { width: sectionRect.width, height: sectionRect.height };
        console.log(
          "[PRINT_VIEW_DEBUG] Using section size directly in print mode",
          {
            sectionSize: canvasSize,
            sectionClassName: sectionElement.className,
          }
        );
      } else {
        // フォールバック：actualSizeを使用
        canvasSize = actualSize;
        console.log("[PRINT_VIEW_DEBUG] No section found, using actualSize", {
          actualSize: canvasSize,
        });
      }
    } else {
      // 通常モード：transformStateを使用
      canvasSize = transformState.transformedSize;

      if (sectionElement) {
        const sectionRect = sectionElement.getBoundingClientRect();
        canvasSize = { width: sectionRect.width, height: sectionRect.height };
        console.log(
          "DrawLayer: Using actual section size for canvas creation",
          {
            transformStateSize: transformState.transformedSize,
            actualSectionSize: canvasSize,
            sizeDifference: {
              width: Math.abs(
                transformState.transformedSize.width - canvasSize.width
              ),
              height: Math.abs(
                transformState.transformedSize.height - canvasSize.height
              ),
            },
          }
        );
      } else {
        console.log("DrawLayer: Section not found, using transformState size", {
          transformStateSize: transformState.transformedSize,
        });
      }
    }

    // actualSizeを即座に更新（canvas作成前に同期）
    console.log(
      "[DRAW_SIZE:CRITICAL] Updating actualSize to match canvasSize BEFORE canvas creation",
      {
        previousActualSize: actualSize,
        newActualSize: canvasSize,
        sectionElementUsed: !!sectionElement,
        willForceUpdate: true,
      }
    );

    // React状態更新の遅延を回避するため、canvasSizeを直接使用
    const effectiveSize = canvasSize;

    // actualSizeを強制的に同期（useStateの代わりに直接更新）
    const updatedActualSize = effectiveSize;
    setActualSize(updatedActualSize);

    // 実際に使用されるサイズの比較（effectiveSize vs canvasSize）
    const sizeComparison = {
      actualSize: { width: actualSize.width, height: actualSize.height },
      canvasSize: { width: canvasSize.width, height: canvasSize.height },
      effectiveSize: effectiveSize, // 実際に使用されるサイズ
      widthDiff: Math.abs(effectiveSize.width - canvasSize.width),
      heightDiff: Math.abs(effectiveSize.height - canvasSize.height),
      sizesMatch:
        effectiveSize.width === canvasSize.width &&
        effectiveSize.height === canvasSize.height,
      canvasUsesEffectiveSize: true, // canvasはeffectiveSizeを使用
      actualSizeWillUpdate: true, // actualSizeは後で更新される
    };

    console.log(
      "[DRAW_CANVAS:INFO] Creating Fabric.js canvas with effectiveSize"
    );
    console.log(
      "[DRAW_SIZE:CRITICAL] SIZE COMPARISON:",
      JSON.stringify(sizeComparison, null, 2)
    );
    console.log(
      "[DRAW_SIZE:INFO] SCALE:",
      transformState?.scale || "N/A (print mode)"
    );
    console.log(
      "[DRAW_SIZE:CRITICAL] Using effectiveSize for canvas creation:",
      effectiveSize
    );

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: effectiveSize.width,
      height: effectiveSize.height,
      backgroundColor: "transparent",
      selection: true, // 常に選択を有効化（editableに関わらず）
      preserveObjectStacking: true,
      // レイアウトズレ防止のための厳密な設定
      allowTouchScrolling: false,
      imageSmoothingEnabled: false,
      enableRetinaScaling: false,
      renderOnAddRemove: true,
      skipTargetFind: false,
      // 選択時のコントロールを最小限に
      selectionBorderColor: "rgba(35, 151, 206, 0.8)",
      selectionLineWidth: 1,
      selectionDashArray: [5, 5],
      // ホバー効果を無効化
      hoverCursor: "default",
      moveCursor: "default",
      // レイアウトに影響しないように
      absolutePositioned: true,
    });

    console.log("DrawLayer: Fabric.js canvas created successfully", {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      canvasSelection: canvas.selection,
    });

    fabricCanvasRef.current = canvas;
    setCanvasInitialized(true); // キャンバス初期化完了フラグを立てる

    // グローバルにcanvas参照とselectionStateRefを保存（透明度保存時に使用）
    window.fabricCanvasRef = fabricCanvasRef;
    window.selectionStateRef = selectionStateRef;

    // 初期化時はloadDrawDataを呼ばない（drawData監視useEffectで処理）
    console.log(
      "[LINE_DEBUG] Canvas initialized, drawData will be loaded by separate useEffect",
      {
        canvasSize: { width: canvas.width, height: canvas.height },
        timestamp: Date.now(),
      }
    );

    return () => {
      console.log("DrawLayer: Disposing canvas");
      if (canvas && !canvas._disposed) {
        canvas.dispose();
      }
      fabricCanvasRef.current = null;
      setCanvasInitialized(false); // クリーンアップ時にフラグをリセット
    };
  }, [transformState?.isTransformApplied, editable]); // drawDataを依存配列から除外（キャンバス再作成を防ぐ）

  // drawDataの変更を監視して既存オブジェクトを更新（キャンバスは再作成しない）
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || canvas._disposed || !canvasInitialized) {
      return; // キャンバスが初期化されていない場合はスキップ
    }

    console.log(
      "[LINE_DEBUG] drawData changed, updating existing objects only",
      {
        dataLength: drawData.length,
        canvasObjectCount: canvas.getObjects().length,
        timestamp: Date.now(),
      }
    );

    // 新しいデータのIDを取得
    const newIds = drawData.map((item) => {
      const normalizedItem = normalizeDrawData(item);
      return normalizedItem.id;
    });

    // キャンバス上の既存オブジェクトのIDを取得
    const existingIds = canvas
      .getObjects()
      .map((obj) => obj.id)
      .filter((id) => id);

    // データから削除されたオブジェクトをキャンバスからも削除
    canvas.getObjects().forEach((obj) => {
      if (obj.id && !newIds.includes(obj.id)) {
        console.log("[LINE_DEBUG] Removing object not in data", {
          id: obj.id,
          type: obj.type,
        });
        canvas.remove(obj);
      }
    });

    // データにあるがキャンバスにないオブジェクトを追加
    drawData.forEach((item) => {
      const normalizedItem = normalizeDrawData(item);

      // 既にキャンバスにある場合はスキップ
      if (existingIds.includes(normalizedItem.id)) {
        return;
      }

      // 新しいオブジェクトを作成（すべてのタイプに対応）
      let fabricObject;

      switch (normalizedItem.type) {
        case "line":
          const lineCanvasWidth = canvas.width;
          const lineCanvasHeight = canvas.height;
          const x1Absolute = (normalizedItem.x1 / 100) * lineCanvasWidth;
          const y1Absolute = (normalizedItem.y1 / 100) * lineCanvasHeight;
          const x2Absolute = (normalizedItem.x2 / 100) * lineCanvasWidth;
          const y2Absolute = (normalizedItem.y2 / 100) * lineCanvasHeight;

          // PathオブジェクトでLineを表現
          // 選択判定用に太いstrokeWidthを設定
          const selectionStrokeWidthForLoad = Math.max(
            normalizedItem.strokeWidth,
            20
          );
          const pathDataForLoad = `M ${x1Absolute} ${y1Absolute} L ${x2Absolute} ${y2Absolute}`;
          const linePathForLoad = new fabric.Path(pathDataForLoad, {
            fill: null,
            stroke: normalizedItem.stroke,
            strokeWidth: selectionStrokeWidthForLoad, // 内部的に太い線として扱う
            strokeUniform: true,
            strokeLineCap: "round",
            strokeLineJoin: "round",
            opacity: normalizedItem.opacity,
            selectable: editable,
            evented: editable,
            objectCaching: false,
            perPixelTargetFind: true, // ピクセル判定を有効化
            hasRotatingPoint: false,
            lockRotation: true,
            lockMovementX: true, // X軸移動を無効化
            lockMovementY: true, // Y軸移動を無効化
            padding: 10, // 選択範囲を広げる
            hoverCursor: "move",
          });

          // カスタムプロパティでLineとして識別
          linePathForLoad.set("isLinePath", true);
          linePathForLoad.set(
            "_originalStrokeWidth",
            normalizedItem.strokeWidth
          ); // 元の太さを保存

          // カスタムレンダリング：実際は細い線として描画
          linePathForLoad._render = function (ctx) {
            const savedStrokeWidth = this.strokeWidth;
            this.strokeWidth = this._originalStrokeWidth || savedStrokeWidth;
            fabric.Path.prototype._render.call(this, ctx);
            this.strokeWidth = savedStrokeWidth;
          };

          // カスタムコントロール（始点・終点）を設定
          setupLinePathControls(linePathForLoad);

          fabricObject = linePathForLoad;

          console.log(
            "[PATH_LINE] Line loaded as Path in drawData useEffect: " +
              JSON.stringify(
                {
                  lineId: normalizedItem.id,
                  pathData: pathDataForLoad,
                  bounds: linePathForLoad.getBoundingRect(),
                  padding: 10,
                },
                null,
                2
              )
          );
          break;

        case "ellipse":
          const ellipseCanvasWidth = canvas.width;
          const ellipseCanvasHeight = canvas.height;
          const ellipseAbsoluteLeft =
            (normalizedItem.x / 100) * ellipseCanvasWidth;
          const ellipseAbsoluteTop =
            (normalizedItem.y / 100) * ellipseCanvasHeight;
          const ellipseAbsoluteWidth =
            (normalizedItem.width / 100) * ellipseCanvasWidth;
          const ellipseAbsoluteHeight =
            (normalizedItem.height / 100) * ellipseCanvasHeight;

          const ellipse = new fabric.Ellipse({
            left: ellipseAbsoluteLeft + ellipseAbsoluteWidth / 2,
            top: ellipseAbsoluteTop + ellipseAbsoluteHeight / 2,
            rx: ellipseAbsoluteWidth / 2,
            ry: ellipseAbsoluteHeight / 2,
            originX: "center",
            originY: "center",
            fill: normalizedItem.fill,
            stroke: normalizedItem.stroke,
            strokeWidth: normalizedItem.strokeWidth,
            strokeUniform: true,
            opacity: normalizedItem.opacity,
            angle: 0,
            selectable: editable,
            evented: editable,
            lockScalingFlip: true,
          });

          // テキストが設定されている場合はテキストも追加
          if (normalizedItem.text && normalizedItem.text.trim()) {
            let ellipseAbsoluteFontSize = normalizedItem.fontSize;
            if (isFontSizeRelative(normalizedItem.fontSize)) {
              ellipseAbsoluteFontSize = convertFontSizeToAbsolute(
                normalizedItem.fontSize,
                ellipseCanvasHeight
              );
            }

            const ellipseTextLeft =
              ellipseAbsoluteLeft +
              calculateTextLeftForGroup(
                ellipseAbsoluteWidth,
                normalizedItem.textAlignH
              );
            const ellipseTextTop =
              ellipseAbsoluteTop +
              calculateTextTopForGroup(
                ellipseAbsoluteHeight,
                normalizedItem.textAlignV
              );

            const ellipseText = new fabric.Text(normalizedItem.text, {
              left: ellipseTextLeft,
              top: ellipseTextTop,
              fontSize: ellipseAbsoluteFontSize,
              fill: normalizedItem.textColor,
              fontWeight: normalizedItem.fontWeight,
              textAlign: normalizedItem.textAlignH,
              originX: getOriginX(normalizedItem.textAlignH),
              originY: getOriginY(normalizedItem.textAlignV),
              selectable: false,
              evented: false,
            });

            fabricObject = new fabric.Group([ellipse, ellipseText], {
              left: ellipseAbsoluteLeft + ellipseAbsoluteWidth / 2,
              top: ellipseAbsoluteTop + ellipseAbsoluteHeight / 2,
              angle: normalizedItem.angle,
              selectable: editable,
              evented: editable,
            });
          } else {
            ellipse.set("angle", normalizedItem.angle);
            fabricObject = ellipse;
          }
          break;

        case "rectangle":
          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;
          const absoluteLeft = (normalizedItem.x / 100) * canvasWidth;
          const absoluteTop = (normalizedItem.y / 100) * canvasHeight;
          const absoluteWidth = (normalizedItem.width / 100) * canvasWidth;
          const absoluteHeight = (normalizedItem.height / 100) * canvasHeight;

          const rect = new fabric.Rect({
            left: absoluteLeft,
            top: absoluteTop,
            width: absoluteWidth,
            height: absoluteHeight,
            fill: normalizedItem.fill,
            stroke: normalizedItem.stroke,
            strokeWidth: normalizedItem.strokeWidth,
            strokeUniform: true,
            opacity: normalizedItem.opacity,
            rx: normalizedItem.cornerRadius || 0,
            ry: normalizedItem.cornerRadius || 0,
            angle: 0,
            selectable: editable,
            evented: editable,
          });

          // テキストが設定されている場合はテキストも追加
          if (normalizedItem.text && normalizedItem.text.trim()) {
            let absoluteFontSize = normalizedItem.fontSize;
            if (isFontSizeRelative(normalizedItem.fontSize)) {
              absoluteFontSize = convertFontSizeToAbsolute(
                normalizedItem.fontSize,
                canvasHeight
              );
            }

            const textLeft = calculateTextLeft(rect, normalizedItem.textAlignH);
            const textTop = calculateTextTop(rect, normalizedItem.textAlignV);

            const text = new fabric.Text(normalizedItem.text, {
              left: textLeft,
              top: textTop,
              fontSize: absoluteFontSize,
              fill: normalizedItem.textColor,
              fontWeight: normalizedItem.fontWeight,
              textAlign: normalizedItem.textAlignH,
              originX: getOriginX(normalizedItem.textAlignH),
              originY: getOriginY(normalizedItem.textAlignV),
              selectable: false,
              evented: false,
            });

            fabricObject = new fabric.Group([rect, text], {
              left: rect.left,
              top: rect.top,
              angle: normalizedItem.angle,
              selectable: editable,
              evented: editable,
            });
          } else {
            rect.set("angle", normalizedItem.angle);
            fabricObject = rect;
          }
          break;

        default:
          return;
      }

      if (fabricObject) {
        fabricObject.set("id", normalizedItem.id);
        canvas.add(fabricObject);
        console.log("[LINE_DEBUG] Added new object to canvas", {
          type: normalizedItem.type,
          id: normalizedItem.id,
        });
      }
    });

    canvas.renderAll();

    // 最後に作成されたオブジェクトを選択（グローバル変数もチェック）
    const lastCreatedObjectId =
      selectionStateRef.current.lastCreatedObjectId ||
      globalLastCreatedObjectId;

    if (lastCreatedObjectId) {
      console.log(
        "[LINE_SELECT_DEBUG] Attempting to select last created object: " +
          JSON.stringify(
            {
              lastCreatedObjectId: lastCreatedObjectId,
              timestamp: Date.now(),
            },
            null,
            2
          )
      );

      // 選択処理を遅延実行してキャンバスのレンダリング完了を待つ
      setTimeout(() => {
        const lastCreatedObject = canvas
          .getObjects()
          .find((obj) => obj.id === lastCreatedObjectId);

        if (lastCreatedObject) {
          canvas.setActiveObject(lastCreatedObject);
          canvas.renderAll();
          console.log(
            "[LINE_SELECT_DEBUG] Last created object selected: " +
              JSON.stringify(
                {
                  objectId: lastCreatedObjectId,
                  objectType: lastCreatedObject.type,
                  isLinePath: lastCreatedObject.isLinePath,
                  timestamp: Date.now(),
                },
                null,
                2
              )
          );

          // オブジェクト選択を通知
          if (onObjectSelected) {
            onObjectSelected(lastCreatedObjectId);
            console.log(
              "[LINE_SELECT_DEBUG] onObjectSelected called for: " +
                lastCreatedObjectId
            );
          }
        } else {
          console.log(
            "[LINE_SELECT_DEBUG] Last created object not found on canvas: " +
              lastCreatedObjectId
          );
        }

        // IDをクリア
        selectionStateRef.current.lastCreatedObjectId = null;
        globalLastCreatedObjectId = null;
      }, 100);
    }
  }, [drawData, editable, canvasInitialized, onObjectSelected]);

  // サイズ変更時の処理（別のuseEffect）- 無効化してcanvasサイズを固定
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || canvas._disposed) {
      console.log("[DRAW_SIZE:INFO] No canvas for size update");
      return;
    }

    const currentWidth = canvas.width;
    const currentHeight = canvas.height;

    console.log(
      "[DRAW_SIZE:INFO] Canvas size update check disabled to maintain effectiveSize",
      {
        currentCanvasSize: { width: currentWidth, height: currentHeight },
        actualSize: actualSize,
        reason: "Canvas should maintain effectiveSize, not follow actualSize",
      }
    );

    // サイズ更新を無効化してcanvasサイズを固定
    // canvas作成時のeffectiveSizeを維持
  }, [actualSize.width, actualSize.height]);

  // イベントリスナーの設定（キャンバス作成後に実行）
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) {
      console.log("DrawLayer: No canvas for event listeners setup");
      return;
    }

    console.log("DrawLayer: Setting up event listeners", {
      isDrawingMode,
      drawingTool,
      editable,
      canvasSize: { width: canvas.width, height: canvas.height },
      hasOnObjectSelected: !!onObjectSelected,
      hasOnObjectDeselected: !!onObjectDeselected,
    });

    // 既存のイベントリスナーをクリア
    canvas.off("mouse:down");
    canvas.off("mouse:move");
    canvas.off("mouse:up");
    canvas.off("selection:created");
    canvas.off("selection:updated");
    canvas.off("selection:cleared");
    canvas.off("object:modified");
    canvas.off("object:moving");

    // 選択イベントリスナーを常に設定（編集可能な場合）
    if (editable) {
      console.log("DrawLayer: Attaching selection event listeners");

      // 関数を直接定義してコールバック依存を回避
      const selectionHandler = (event) => {
        console.log("DrawLayer: handleObjectSelection called", {
          eventType: event.type,
          hasSelected: !!(event.selected && event.selected.length > 0),
          hasTarget: !!event.target,
          timestamp: Date.now(),
        });

        // 選択変更前に現在の状態を保存（selection:updatedの場合）
        if (event.type === "selection:updated") {
          console.log(
            "DrawLayer: Selection updated - saving previous state before switching"
          );
          saveCurrentCanvasState();
        }

        const selectedObject = event.selected?.[0] || event.target;
        console.log("DrawLayer: selectedObject check", {
          hasSelectedObject: !!selectedObject,
          selectedObjectId: selectedObject?.id,
          selectedObjectType: selectedObject?.type,
        });

        // 選択時の詳細構造ログ
        if (selectedObject && selectedObject.type === "group") {
          const structureData = {
            groupId: selectedObject.id,
            groupType: selectedObject.type,
            groupPosition: {
              left: selectedObject.left,
              top: selectedObject.top,
            },
            groupSize: {
              width: selectedObject.width,
              height: selectedObject.height,
            },
            groupScale: {
              scaleX: selectedObject.scaleX,
              scaleY: selectedObject.scaleY,
            },
            groupAngle: selectedObject.angle,
            objectsCount: selectedObject._objects?.length,
            objects: selectedObject._objects?.map((obj) => ({
              type: obj.type,
              position: { left: obj.left, top: obj.top },
              size: { width: obj.width, height: obj.height },
              scale: { scaleX: obj.scaleX, scaleY: obj.scaleY },
              angle: obj.angle,
              text: obj.type === "text" ? obj.text : undefined,
              textAlign: obj.type === "text" ? obj.textAlign : undefined,
              originX: obj.type === "text" ? obj.originX : undefined,
              originY: obj.type === "text" ? obj.originY : undefined,
              fontSize: obj.type === "text" ? obj.fontSize : undefined,
              fill: obj.type === "text" ? obj.fill : obj.fill,
              stroke: obj.type === "rect" ? obj.stroke : undefined,
              strokeWidth: obj.type === "rect" ? obj.strokeWidth : undefined,
            })),
            selectionProperties: {
              hasControls: selectedObject.hasControls,
              hasBorders: selectedObject.hasBorders,
              subTargetCheck: selectedObject.subTargetCheck,
              interactive: selectedObject.interactive,
              selectable: selectedObject.selectable,
              evented: selectedObject.evented,
            },
            timestamp: Date.now(),
          };
          console.log(
            "[SELECTION_STRUCTURE_DEBUG] Object SELECTED - detailed structure:",
            JSON.stringify(structureData, null, 2)
          );
        }

        if (selectedObject && selectedObject.id) {
          // 選択されたオブジェクトのスタイルをcurrentStyleに反映（テキスト情報も含む）
          let objectStyle = {
            strokeWidth: 2,
            stroke: "#000000",
            fill: "transparent",
            opacity: 1,
            text: "",
            textColor: "#000000",
            fontSize: 16,
            fontWeight: 400,
            textAlignH: "center",
            textAlignV: "middle",
            cornerRadius: 0,
          };

          // グループオブジェクトの場合、図形とテキストの情報を個別に取得
          if (selectedObject.type === "group" && selectedObject._objects) {
            const rectObject = selectedObject._objects.find(
              (obj) => obj.type === "rect"
            );
            const ellipseObject = selectedObject._objects.find(
              (obj) => obj.type === "ellipse"
            );
            const shapeObject = rectObject || ellipseObject;
            const textObject = selectedObject._objects.find(
              (obj) => obj.type === "text"
            );

            console.log("DrawLayer: Group object selected, extracting styles", {
              shapeType: shapeObject?.type,
              hasShape: !!shapeObject,
              hasText: !!textObject,
              shapeFill: shapeObject?.fill,
              shapeStroke: shapeObject?.stroke,
              textContent: textObject?.text,
            });

            // 図形のスタイル情報を取得
            if (shapeObject) {
              objectStyle.strokeWidth = shapeObject.strokeWidth || 2;
              objectStyle.stroke = shapeObject.stroke || "#000000";
              objectStyle.fill = shapeObject.fill || "transparent";
              objectStyle.opacity = shapeObject.opacity || 1;
              objectStyle.cornerRadius = rectObject ? rectObject.rx || 0 : 0; // 角の丸み情報を取得（長方形のみ）
              objectStyle.shapeType = ellipseObject ? "ellipse" : "rectangle"; // オブジェクトタイプを設定

              // 色から透明度を抽出
              objectStyle.fillOpacity = extractOpacityFromColor(
                shapeObject.fill
              );
              objectStyle.strokeOpacity = extractOpacityFromColor(
                shapeObject.stroke
              );
            }

            // テキストの情報を取得
            if (textObject) {
              objectStyle.text = textObject.text || "";
              objectStyle.textColor = textObject.fill || "#000000";
              objectStyle.fontSize = textObject.fontSize || 16;
              objectStyle.fontWeight = textObject.fontWeight || 400;
              objectStyle.textAlignH = textObject.textAlign || "center";
              objectStyle.textAlignV =
                textObject.originY === "top"
                  ? "top"
                  : textObject.originY === "bottom"
                    ? "bottom"
                    : "middle";
            }

            // 回転角度を取得（グループ全体から）
            objectStyle.angle = selectedObject.angle || 0;
          } else if (
            selectedObject.type === "path" &&
            selectedObject.isLinePath
          ) {
            // Path直線の場合
            objectStyle.strokeWidth =
              selectedObject._originalStrokeWidth ||
              selectedObject.strokeWidth ||
              2;
            objectStyle.stroke = selectedObject.stroke || "#000000";
            objectStyle.opacity = selectedObject.opacity || 1;
            objectStyle.shapeType = "line"; // オブジェクトタイプを直線として設定

            // 色から透明度を抽出
            objectStyle.strokeOpacity = extractOpacityFromColor(
              selectedObject.stroke
            );

            console.log(
              "DrawLayer: Path line selected, extracting styles: " +
                JSON.stringify(
                  {
                    lineId: selectedObject.id,
                    strokeWidth: objectStyle.strokeWidth,
                    stroke: objectStyle.stroke,
                    opacity: objectStyle.opacity,
                  },
                  null,
                  2
                )
            );
          } else {
            // 単一オブジェクトの場合（長方形・楕円）
            objectStyle.strokeWidth = selectedObject.strokeWidth || 2;
            objectStyle.stroke = selectedObject.stroke || "#000000";
            objectStyle.fill = selectedObject.fill || "transparent";
            objectStyle.opacity = selectedObject.opacity || 1;
            objectStyle.angle = selectedObject.angle || 0;
            objectStyle.cornerRadius = selectedObject.rx || 0; // 角の丸み情報を取得
            objectStyle.shapeType =
              selectedObject.type === "ellipse" ? "ellipse" : "rectangle"; // オブジェクトタイプを設定

            // 色から透明度を抽出
            objectStyle.fillOpacity = extractOpacityFromColor(
              selectedObject.fill
            );
            objectStyle.strokeOpacity = extractOpacityFromColor(
              selectedObject.stroke
            );
          }

          console.log("DrawLayer: Extracted object style", {
            objectId: selectedObject.id,
            objectType: selectedObject.type,
            extractedStyle: objectStyle,
          });

          setCurrentStyle(objectStyle);

          console.log("DrawLayer: Before onObjectSelected call", {
            hasOnObjectSelected: !!onObjectSelected,
            objectId: selectedObject.id,
          });

          // オブジェクト選択を通知
          if (onObjectSelected) {
            onObjectSelected(selectedObject.id);
            console.log("DrawLayer: onObjectSelected called successfully");
          } else {
            console.log("DrawLayer: onObjectSelected is not available");
          }

          console.log("DrawLayer: Object selected, style updated", {
            objectId: selectedObject.id,
            objectStyle,
          });
        } else {
          console.log("DrawLayer: No valid selectedObject found");
        }
      };

      const selectionClearedHandler = (event) => {
        console.log("DrawLayer: SELECTION CLEARED", {
          timestamp: Date.now(),
          hasPendingNewObject: !!selectionStateRef.current.pendingNewObject,
          eventTarget: event?.e?.target?.tagName,
          groupCreating: !!window.drawLayerGroupCreating,
        });

        // グループ作成中は選択解除を無視
        if (window.drawLayerGroupCreating) {
          console.log(
            "DrawLayer: Selection cleared during group creation, ignoring",
            {
              groupCreatingFlag: window.drawLayerGroupCreating,
              timestamp: Date.now(),
            }
          );
          return;
        }

        // スタイル変更中は選択解除を無視
        if (event && event.e) {
          const target = event.e.target;
          if (target) {
            const isStylePanelElement =
              target.closest("[data-style-panel]") ||
              target.closest("input") ||
              target.closest("button") ||
              target.closest("label") ||
              target.closest("span") ||
              target.hasAttribute("data-style-panel") ||
              target.tagName === "INPUT" ||
              target.tagName === "BUTTON" ||
              target.tagName === "LABEL";

            if (isStylePanelElement) {
              console.log(
                "DrawLayer: Selection cleared by style panel interaction, ignoring",
                {
                  tagName: target.tagName,
                  className: target.className,
                  hasDataStylePanel: target.hasAttribute("data-style-panel"),
                  closestStylePanel: !!target.closest("[data-style-panel]"),
                }
              );
              return;
            }
          }
        }

        // 保留中の新しいオブジェクトがある場合は保存
        if (selectionStateRef.current.pendingNewObject) {
          const pendingObject = selectionStateRef.current.pendingNewObject;
          console.log(
            "DrawLayer: Saving pending new object on selection clear",
            {
              objectId: pendingObject.objectId,
              drawData: pendingObject.drawData,
              timestamp: pendingObject.timestamp,
            }
          );

          const updatedData = [...drawData, pendingObject.drawData];
          if (onDrawDataChange) {
            onDrawDataChange(updatedData);
            console.log("DrawLayer: Pending object saved to data", {
              newDataLength: updatedData.length,
              savedObject: pendingObject.drawData,
            });
          }

          // 保留状態をクリア
          selectionStateRef.current.pendingNewObject = null;
        } else {
          // 通常の選択解除時にデータを保存

          // 直線かどうかをチェック（キャンバスから取得）
          const canvas = fabricCanvasRef.current;
          let wasLineObject = false;
          if (canvas && selectionStateRef.current.selectedObjectId) {
            const lastSelectedObj = canvas
              .getObjects()
              .find(
                (obj) => obj.id === selectionStateRef.current.selectedObjectId
              );
            wasLineObject = lastSelectedObj?.type === "line";
          }

          console.log(
            "[SIZE_DEBUG] DrawLayer selection cleared - container size check",
            {
              containerElement: containerRef.current,
              containerSize: containerRef.current
                ? {
                    offsetWidth: containerRef.current.offsetWidth,
                    offsetHeight: containerRef.current.offsetHeight,
                    clientWidth: containerRef.current.clientWidth,
                    clientHeight: containerRef.current.clientHeight,
                    getBoundingClientRect:
                      containerRef.current.getBoundingClientRect(),
                  }
                : null,
              actualSizeState: actualSize,
              wasLineObject: wasLineObject,
              timestamp: Date.now(),
            }
          );

          saveCurrentCanvasState();

          console.log("[SIZE_DEBUG] DrawLayer selection cleared - data saved", {
            timestamp: Date.now(),
            action: "SELECTION_CLEARED_DATA_SAVED",
            wasLineObject: wasLineObject,
          });
        }

        // オブジェクト選択解除を通知
        if (onObjectDeselected) {
          onObjectDeselected();
        }

        // デフォルトスタイルに戻す
        const defaultStyle = {
          strokeWidth: 2,
          stroke: "#000000",
          fill: "transparent",
          opacity: 1,
        };

        setCurrentStyle(defaultStyle);

        console.log(
          "DrawLayer: Selection cleared, style reset to default, data saved"
        );
      };

      canvas.on("selection:created", selectionHandler);
      canvas.on("selection:updated", selectionHandler);
      canvas.on("selection:cleared", selectionClearedHandler);
      canvas.on("object:modified", handleObjectModified);
      canvas.on("object:moving", handleObjectMoving);
      console.log("DrawLayer: Selection event listeners attached successfully");
    }

    // ドローモード時のみ描画イベントリスナーを設定
    if (editable && isDrawingMode) {
      setupDrawingEvents(canvas);
    }
  }, [
    isDrawingMode,
    drawingTool,
    editable,
    fabricCanvasRef.current,
    onObjectSelected,
    onObjectDeselected,
  ]);

  // 描画イベントの設定
  const setupDrawingEvents = (canvas) => {
    console.log("DrawLayer: setupDrawingEvents called", canvas);
    canvas.on("mouse:down", handleMouseDown);
    canvas.on("mouse:move", handleMouseMove);
    canvas.on("mouse:up", handleMouseUp);
    console.log("DrawLayer: Event listeners attached", canvas);
  };

  // キーボードイベントの設定
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        // テキストフィールドにフォーカスがある場合は削除処理をスキップ
        const activeElement = document.activeElement;
        if (
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.contentEditable === "true")
        ) {
          console.log(
            "DrawLayer: Text field has focus, skipping object deletion",
            {
              activeElementTag: activeElement.tagName,
              activeElementType: activeElement.type,
              contentEditable: activeElement.contentEditable,
            }
          );
          return;
        }

        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.id) {
          // キャンバスからオブジェクトを削除
          canvas.remove(activeObject);
          canvas.renderAll();

          // データからも削除（最適化版対応）
          const updatedData = drawData.filter((item) => {
            const normalizedItem = normalizeDrawData(item);
            return normalizedItem.id !== activeObject.id;
          });
          if (onDrawDataChange) {
            onDrawDataChange(updatedData);
          }
          console.log("DrawLayer: Object deleted", activeObject.id);
        }
      }
    };

    if (editable) {
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [editable, drawData, onDrawDataChange]);

  // マウスダウンイベント
  const handleMouseDown = (event) => {
    console.log(
      "[MOUSE_EVENT_DEBUG] handleMouseDown called: " +
        JSON.stringify(
          {
            isDrawingMode,
            drawingTool,
            eventType: event.e?.type,
            currentIsDrawing: drawingStateRef.current.isDrawing,
            timestamp: Date.now(),
          },
          null,
          2
        )
    );

    // スタイルパネル内のクリックかチェック
    const target = event.e?.target;
    if (target) {
      // スタイルパネル内の要素かチェック（より包括的に）
      const isStylePanelElement =
        target.closest("[data-style-panel]") ||
        target.closest("input") ||
        target.closest("button") ||
        target.closest("label") ||
        target.closest("span") ||
        target.hasAttribute("data-style-panel") ||
        target.tagName === "INPUT" ||
        target.tagName === "BUTTON" ||
        target.tagName === "LABEL";

      if (isStylePanelElement) {
        console.log("DrawLayer: Click on style panel element, ignoring", {
          tagName: target.tagName,
          className: target.className,
          hasDataStylePanel: target.hasAttribute("data-style-panel"),
          closestStylePanel: !!target.closest("[data-style-panel]"),
        });
        return;
      }
    }

    if (
      !isDrawingMode ||
      (drawingTool !== "rectangle" &&
        drawingTool !== "ellipse" &&
        drawingTool !== "line")
    ) {
      console.log("DrawLayer: handleMouseDown early return", {
        isDrawingMode,
        drawingTool,
      });
      return;
    }

    const canvas = fabricCanvasRef.current;

    // 既に描画中かチェック
    if (
      drawingStateRef.current.isDrawing ||
      drawingStateRef.current.drawingObject
    ) {
      console.log("DrawLayer: Already drawing, ignoring mousedown");
      return;
    }

    const pointer = canvas.getPointer(event.e);
    console.log("DrawLayer: Setting isDrawing to true", pointer);

    // refで状態を管理
    drawingStateRef.current.isDrawing = true;
    drawingStateRef.current.startPoint = pointer;
    drawingStateRef.current.sizeUpdatePaused = true; // サイズ更新を一時停止

    setIsDrawing(true);
    setStartPoint(pointer);

    console.log("[DRAW_RESIZE:INFO] Size update paused - drawing started", {
      timestamp: Date.now(),
    });

    // 色に別々の透明度を適用
    const fillWithOpacity = applyOpacityToColor(
      currentStyle.fill,
      currentStyle.fillOpacity
    );
    const strokeWithOpacity = applyOpacityToColor(
      currentStyle.stroke,
      currentStyle.strokeOpacity
    );

    let drawingObject;

    if (drawingTool === "ellipse") {
      // 楕円を作成（デフォルトのcenter基準を使用）
      drawingObject = new fabric.Ellipse({
        left: pointer.x,
        top: pointer.y,
        rx: 0, // 水平半径
        ry: 0, // 垂直半径
        originX: "center",
        originY: "center",
        fill: fillWithOpacity,
        stroke: strokeWithOpacity,
        strokeWidth: currentStyle.strokeWidth,
        strokeUniform: true,
        strokeDashArray: null,
        strokeLineCap: "butt",
        strokeLineJoin: "miter",
        strokeMiterLimit: 4,
        paintFirst: "fill",
        opacity: currentStyle.opacity,
        selectable: false,
        evented: false,
      });

      console.log("DrawLayer: Ellipse added to canvas");
    } else if (drawingTool === "line") {
      // 直線を作成（Pathオブジェクトで実装）
      // 選択判定用に太いstrokeWidthを設定
      const selectionStrokeWidthForDraw = Math.max(
        currentStyle.strokeWidth,
        20
      );
      const linePathData = `M ${pointer.x} ${pointer.y} L ${pointer.x} ${pointer.y}`;
      drawingObject = new fabric.Path(linePathData, {
        fill: null,
        stroke: strokeWithOpacity,
        strokeWidth: selectionStrokeWidthForDraw, // 内部的に太い線として扱う
        strokeUniform: true,
        strokeLineCap: "round",
        strokeLineJoin: "round",
        opacity: currentStyle.opacity,
        selectable: false,
        evented: false,
        objectCaching: false,
        perPixelTargetFind: true, // ピクセル判定を有効化
        lockMovementX: true, // X軸移動を無効化
        lockMovementY: true, // Y軸移動を無効化
        padding: 10, // 選択範囲を広げる
      });
      drawingObject.set("isLinePath", true); // カスタムプロパティで識別
      drawingObject.set("_originalStrokeWidth", currentStyle.strokeWidth); // 元の太さを保存

      // カスタムレンダリング：実際は細い線として描画
      drawingObject._render = function (ctx) {
        const savedStrokeWidth = this.strokeWidth;
        this.strokeWidth = this._originalStrokeWidth || savedStrokeWidth;
        fabric.Path.prototype._render.call(this, ctx);
        this.strokeWidth = savedStrokeWidth;
      };

      console.log("DrawLayer: Line Path added to canvas");
    } else {
      // シンプルな単一長方形に戻す（重なりを避けるためにpaintFirstで塗りつぶしを先に描画）
      drawingObject = new fabric.Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: fillWithOpacity,
        stroke: strokeWithOpacity,
        strokeWidth: currentStyle.strokeWidth,
        strokeUniform: true, // 枠線の太さを一定に保つ
        strokeDashArray: null,
        strokeLineCap: "butt",
        strokeLineJoin: "miter",
        strokeMiterLimit: 4,
        paintFirst: "fill", // 塗りつぶしを先に描画して重なりを最小化
        opacity: currentStyle.opacity,
        rx: currentStyle.cornerRadius || 0, // 角の丸みを適用
        ry: currentStyle.cornerRadius || 0, // 角の丸みを適用
        selectable: false,
        evented: false,
      });

      console.log("DrawLayer: Rectangle added to canvas");
    }

    // カスタムプロパティを設定
    drawingObject._isBeingDrawn = true;
    drawingObject.isBeingDrawn = true;

    // refに保存
    drawingStateRef.current.drawingObject = drawingObject;

    canvas.add(drawingObject);
    canvas.setActiveObject(drawingObject);
    canvas.renderAll();

    console.log("DrawLayer: Drawing object added to canvas", {
      type: drawingTool,
      isBeingDrawn: drawingObject._isBeingDrawn,
      objectId: drawingObject.id,
      totalObjects: canvas.getObjects().length,
    });
  };

  // マウス移動イベント
  const handleMouseMove = (event) => {
    console.log("DrawLayer: handleMouseMove called", {
      refIsDrawing: drawingStateRef.current.isDrawing,
      refHasStartPoint: !!drawingStateRef.current.startPoint,
      refHasDrawingObject: !!drawingStateRef.current.drawingObject,
      eventType: event.e?.type,
    });

    // refから状態を取得
    if (
      !drawingStateRef.current.isDrawing ||
      !drawingStateRef.current.startPoint ||
      !drawingStateRef.current.drawingObject
    ) {
      console.log("DrawLayer: handleMouseMove early return - not drawing");
      return;
    }

    const canvas = fabricCanvasRef.current;
    const pointer = canvas.getPointer(event.e);
    const drawingObject = drawingStateRef.current.drawingObject;
    const startPoint = drawingStateRef.current.startPoint;

    console.log("DrawLayer: handleMouseMove pointer", pointer);

    const width = Math.abs(pointer.x - startPoint.x);
    const height = Math.abs(pointer.y - startPoint.y);
    const left = Math.min(pointer.x, startPoint.x);
    const top = Math.min(pointer.y, startPoint.y);

    if (drawingObject.type === "ellipse") {
      // 楕円のサイズを更新（rx, ryは半径なので半分）
      // originX/Yが'center'なので、leftとtopは中心座標
      drawingObject.set({
        left: left + width / 2,
        top: top + height / 2,
        rx: width / 2,
        ry: height / 2,
      });
      console.log("DrawLayer: Ellipse resized", {
        center: { left: left + width / 2, top: top + height / 2 },
        rx: width / 2,
        ry: height / 2,
        boundingBox: { left, top, width, height },
      });
    } else if (drawingObject.type === "line" || drawingObject.type === "path") {
      // 直線またはPath直線の終点を更新
      if (drawingObject.type === "path" && drawingObject.isLinePath) {
        // Pathオブジェクトの場合：pathDataを更新
        const newPathData = `M ${startPoint.x} ${startPoint.y} L ${pointer.x} ${pointer.y}`;
        drawingObject.set("path", fabric.util.parsePath(newPathData));
        console.log("DrawLayer: Line Path resized", {
          start: { x: startPoint.x, y: startPoint.y },
          end: { x: pointer.x, y: pointer.y },
          pathData: newPathData,
        });
      } else {
        // Lineオブジェクトの場合（後方互換性）
        drawingObject.set({
          x2: pointer.x,
          y2: pointer.y,
        });
        console.log("DrawLayer: Line resized", {
          start: { x: drawingObject.x1, y: drawingObject.y1 },
          end: { x: pointer.x, y: pointer.y },
        });
      }
    } else {
      // 単一長方形のサイズを更新
      drawingObject.set({
        left,
        top,
        width,
        height,
      });
      console.log("DrawLayer: Rectangle resized", { width, height, left, top });
    }

    canvas.renderAll();
  };

  // マウスアップイベント
  const handleMouseUp = (event) => {
    console.log("DrawLayer: handleMouseUp called", {
      refIsDrawing: drawingStateRef.current.isDrawing,
      refHasDrawingObject: !!drawingStateRef.current.drawingObject,
    });

    // refから状態を確認
    if (
      !drawingStateRef.current.isDrawing ||
      !drawingStateRef.current.drawingObject
    ) {
      console.log("DrawLayer: handleMouseUp early return - not drawing");
      return;
    }

    const canvas = fabricCanvasRef.current;
    const drawingObject = drawingStateRef.current.drawingObject;
    const isEllipse = drawingObject.type === "ellipse";
    const isLine =
      drawingObject.type === "line" ||
      (drawingObject.type === "path" && drawingObject.isLinePath);
    const isPathLine =
      drawingObject.type === "path" && drawingObject.isLinePath;

    // width/height判定用の値を事前に計算
    let displayWidth, displayHeight;
    if (isEllipse) {
      displayWidth = drawingObject.rx * 2;
      displayHeight = drawingObject.ry * 2;
    } else if (isLine) {
      if (isPathLine) {
        // Path直線の場合：pathコマンドから直接座標を取得
        const pathCommands = drawingObject.path;
        if (pathCommands && pathCommands.length >= 2) {
          const x1 = pathCommands[0][1];
          const y1 = pathCommands[0][2];
          const x2 = pathCommands[1][1];
          const y2 = pathCommands[1][2];
          displayWidth = Math.abs(x2 - x1);
          displayHeight = Math.abs(y2 - y1);
        } else {
          displayWidth = 0;
          displayHeight = 0;
        }
      } else {
        displayWidth = Math.abs(drawingObject.x2 - drawingObject.x1);
        displayHeight = Math.abs(drawingObject.y2 - drawingObject.y1);
      }
    } else {
      displayWidth = drawingObject.width;
      displayHeight = drawingObject.height;
    }

    console.log(
      "[LINE_CONTROL_DEBUG] Line dimensions: " +
        JSON.stringify(
          {
            displayWidth,
            displayHeight,
            left: drawingObject.left,
            top: drawingObject.top,
          },
          null,
          2
        )
    );

    // 描画フラグをクリア
    drawingObject.set("_isBeingDrawn", false);
    drawingObject.set("isBeingDrawn", false);

    // 最小サイズチェック（ドラッグしていない場合は削除）
    // displayWidthとdisplayHeightを再利用
    if (displayWidth < 5 && displayHeight < 5) {
      canvas.remove(drawingObject);
      console.log(
        "[DRAW_LINE] Drawing object too small, removed: " +
          JSON.stringify(
            {
              type: drawingObject.type,
              isPathLine: isPathLine,
              width: displayWidth,
              height: displayHeight,
            },
            null,
            2
          )
      );
    } else {
      // 相対座標に変換（canvasの実際のサイズを使用してタイミング問題を回避）
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // タイプに応じて座標を計算
      let leftPercent, topPercent, widthPercent, heightPercent;

      if (isLine) {
        // 直線の場合：始点と終点の座標を保存
        let x1, y1, x2, y2;

        console.log("[PATH_LINE_DEBUG] Processing line in handleMouseUp", {
          isPathLine,
          drawingObjectType: drawingObject.type,
          isLinePath: drawingObject.isLinePath,
          hasPath: !!drawingObject.path,
          pathLength: drawingObject.path?.length,
        });

        if (isPathLine) {
          // Path直線の場合：pathから座標を抽出
          const pathCommands = drawingObject.path;
          console.log("[PATH_LINE_DEBUG] Path commands check", {
            hasPathCommands: !!pathCommands,
            pathCommandsLength: pathCommands?.length,
            pathCommands: pathCommands,
          });

          if (pathCommands && pathCommands.length >= 2) {
            // M x y (始点) と L x y (終点) を抽出
            x1 = pathCommands[0][1]; // M命令のx
            y1 = pathCommands[0][2]; // M命令のy
            x2 = pathCommands[1][1]; // L命令のx
            y2 = pathCommands[1][2]; // L命令のy

            console.log("[PATH_LINE] Extracted coordinates from Path", {
              pathCommands: pathCommands,
              extractedCoords: { x1, y1, x2, y2 },
            });
          } else {
            console.error("[PATH_LINE] Invalid path data - RETURNING", {
              pathCommands,
              length: pathCommands?.length,
            });
            return; // 無効なパスの場合は処理を中断
          }
        } else {
          // Line直線の場合（後方互換性）
          x1 = drawingObject.x1;
          y1 = drawingObject.y1;
          x2 = drawingObject.x2;
          y2 = drawingObject.y2;
        }

        const x1Percent = (x1 / canvasWidth) * 100;
        const y1Percent = (y1 / canvasHeight) * 100;
        const x2Percent = (x2 / canvasWidth) * 100;
        const y2Percent = (y2 / canvasHeight) * 100;

        console.log(
          "[DRAW_TIMING] Using canvas size for line coordinate conversion",
          {
            canvasSize: { width: canvasWidth, height: canvasHeight },
            lineType: isPathLine ? "path" : "line",
            linePoints: { x1, y1, x2, y2 },
            calculatedPercent: { x1Percent, y1Percent, x2Percent, y2Percent },
            timestamp: Date.now(),
          }
        );

        // 直線用のデータ形式（'l'タイプ）
        const shortId =
          Date.now().toString(36) + Math.random().toString(36).substr(2, 3);
        // 元の太さを取得（カスタムレンダリング用に保存した値）
        const actualLineStrokeWidth =
          drawingObject._originalStrokeWidth || currentStyle.strokeWidth;
        let newDrawItem = [
          "l", // type: line
          shortId,
          Math.round(x1Percent * 100) / 100,
          Math.round(y1Percent * 100) / 100,
          Math.round(x2Percent * 100) / 100,
          Math.round(y2Percent * 100) / 100,
          drawingObject.stroke !== "#000000" ? drawingObject.stroke : null,
          actualLineStrokeWidth !== 2 ? actualLineStrokeWidth : null,
          drawingObject.opacity !== 1 ? drawingObject.opacity : null,
        ];

        // 末尾のnullを削除
        while (
          newDrawItem.length > 6 &&
          newDrawItem[newDrawItem.length - 1] === null
        ) {
          newDrawItem.pop();
        }

        console.log(
          "[SAVE_DEBUG] Line data created in handleMouseUp: " +
            JSON.stringify(
              {
                lineId: `draw_${shortId}`,
                lineData: newDrawItem,
                timestamp: Date.now(),
              },
              null,
              2
            )
        );

        // 描画完了時にleft/topがある場合はpathに統合
        if (drawingObject.left !== 0 || drawingObject.top !== 0) {
          const adjustedX1 = x1 + drawingObject.left;
          const adjustedY1 = y1 + drawingObject.top;
          const adjustedX2 = x2 + drawingObject.left;
          const adjustedY2 = y2 + drawingObject.top;

          const adjustedPathData = `M ${adjustedX1} ${adjustedY1} L ${adjustedX2} ${adjustedY2}`;
          drawingObject.set("path", fabric.util.parsePath(adjustedPathData));

          console.log(
            "[PATH_LINE] Drawing complete - adjusting path for left/top offset: " +
              JSON.stringify(
                {
                  originalPath: { x1, y1, x2, y2 },
                  offset: { left: drawingObject.left, top: drawingObject.top },
                  adjustedPath: {
                    x1: adjustedX1,
                    y1: adjustedY1,
                    x2: adjustedX2,
                    y2: adjustedY2,
                  },
                  timestamp: Date.now(),
                },
                null,
                2
              )
          );

          // 調整後の座標を使う
          x1 = adjustedX1;
          y1 = adjustedY1;
          x2 = adjustedX2;
          y2 = adjustedY2;

          // パーセンテージも再計算
          const x1Percent = (x1 / canvasWidth) * 100;
          const y1Percent = (y1 / canvasHeight) * 100;
          const x2Percent = (x2 / canvasWidth) * 100;
          const y2Percent = (y2 / canvasHeight) * 100;

          // データも更新
          newDrawItem = [
            "l",
            shortId,
            Math.round(x1Percent * 100) / 100,
            Math.round(y1Percent * 100) / 100,
            Math.round(x2Percent * 100) / 100,
            Math.round(y2Percent * 100) / 100,
            drawingObject.stroke !== "#000000" ? drawingObject.stroke : null,
            actualLineStrokeWidth !== 2 ? actualLineStrokeWidth : null,
            drawingObject.opacity !== 1 ? drawingObject.opacity : null,
          ];

          while (
            newDrawItem.length > 6 &&
            newDrawItem[newDrawItem.length - 1] === null
          ) {
            newDrawItem.pop();
          }
        }

        // オブジェクトのプロパティを更新
        const objectId = `draw_${shortId}`;
        drawingObject.set({
          id: objectId,
          selectable: true,
          evented: true,
          perPixelTargetFind: true, // ピクセル判定を有効化
          hasRotatingPoint: false,
          lockRotation: true,
          padding: 10, // 選択範囲を広げる
          hoverCursor: "move",
        });

        // left/topを直接プロパティで0に設定（setではなく直接代入）
        drawingObject.left = 0;
        drawingObject.top = 0;

        // 座標を更新（この時点でFabric.jsがleft/topを再計算しないように）
        drawingObject.setCoords();

        // setCoords後に再度left/topを強制的に0に
        drawingObject.left = 0;
        drawingObject.top = 0;

        console.log(
          "[PATH_LINE] Line properties set with forced left/top=0: " +
            JSON.stringify(
              {
                lineId: objectId,
                finalLeftTop: {
                  left: drawingObject.left,
                  top: drawingObject.top,
                },
                finalPathCoords: { x1, y1, x2, y2 },
                timestamp: Date.now(),
              },
              null,
              2
            )
        );

        // 保存前に最後に作成した直線を選択するためのIDをグローバルに保存
        globalLastCreatedObjectId = objectId;
        selectionStateRef.current.lastCreatedObjectId = objectId;

        // 最後に保存（選択よりも先に）
        const updatedData = [...drawData, newDrawItem];
        if (onDrawDataChange) {
          onDrawDataChange(updatedData);
          console.log(
            "DrawLayer: Line saved BEFORE selection setup: " +
              JSON.stringify(
                {
                  newDataLength: updatedData.length,
                  savedObject: newDrawItem,
                  objectId: objectId,
                  timestamp: Date.now(),
                },
                null,
                2
              )
          );
        }

        // 保存後に遅延実行でコントロール設定と選択処理
        setTimeout(() => {
          // カスタムコントロール（始点・終点）を設定
          setupLinePathControls(drawingObject);

          console.log(
            "[PATH_LINE] Line properties set for selection (delayed): " +
              JSON.stringify(
                {
                  lineId: objectId,
                  selectable: true,
                  padding: 10,
                  hasControls: true,
                },
                null,
                2
              )
          );

          // 新しく作成されたオブジェクトを選択
          canvas.setActiveObject(drawingObject);
          canvas.renderAll();

          console.log(
            "[PATH_LINE_FINAL] Line selected and rendered: " +
              JSON.stringify(
                {
                  lineId: objectId,
                  leftTop: { left: drawingObject.left, top: drawingObject.top },
                  timestamp: Date.now(),
                },
                null,
                2
              )
          );

          // オブジェクト作成完了を通知
          if (onObjectCreated) {
            onObjectCreated(objectId);
          }

          // オブジェクト選択を通知
          if (onObjectSelected) {
            onObjectSelected(objectId);
          }
        }, 50);

        // 描画状態をクリアして早期リターン
        drawingStateRef.current.isDrawing = false;
        drawingStateRef.current.startPoint = null;
        drawingStateRef.current.drawingObject = null;
        drawingStateRef.current.sizeUpdatePaused = false;
        drawingStateRef.current.recentlyCompleted = true;
        drawingStateRef.current.completionTimestamp = Date.now();

        setIsDrawing(false);
        setStartPoint(null);
        canvas.renderAll();

        setTimeout(() => {
          drawingStateRef.current.recentlyCompleted = false;
          drawingStateRef.current.completionTimestamp = null;
        }, 3000);

        return; // 直線の場合はここで終了
      } else if (isEllipse) {
        // 楕円の場合：originX/Yが'center'なので、leftとtopは中心座標
        const ellipseWidth = drawingObject.rx * 2;
        const ellipseHeight = drawingObject.ry * 2;
        const ellipseLeft = drawingObject.left - drawingObject.rx;
        const ellipseTop = drawingObject.top - drawingObject.ry;

        leftPercent = (ellipseLeft / canvasWidth) * 100;
        topPercent = (ellipseTop / canvasHeight) * 100;
        widthPercent = (ellipseWidth / canvasWidth) * 100;
        heightPercent = (ellipseHeight / canvasHeight) * 100;

        console.log(
          "[DRAW_TIMING] Using canvas size for ellipse coordinate conversion",
          {
            canvasSize: { width: canvasWidth, height: canvasHeight },
            ellipseOrigin: "left-top",
            ellipsePosition: {
              left: drawingObject.left,
              top: drawingObject.top,
            },
            ellipseRadius: { rx: drawingObject.rx, ry: drawingObject.ry },
            ellipseSize: { width: ellipseWidth, height: ellipseHeight },
            calculatedPercent: {
              leftPercent,
              topPercent,
              widthPercent,
              heightPercent,
            },
            timestamp: Date.now(),
          }
        );
      } else {
        leftPercent = (drawingObject.left / canvasWidth) * 100;
        topPercent = (drawingObject.top / canvasHeight) * 100;
        widthPercent = (drawingObject.width / canvasWidth) * 100;
        heightPercent = (drawingObject.height / canvasHeight) * 100;

        console.log(
          "[DRAW_TIMING] Using canvas size for rectangle coordinate conversion",
          {
            canvasSize: { width: canvasWidth, height: canvasHeight },
            actualSizeState: actualSize,
            rectPosition: {
              left: drawingObject.left,
              top: drawingObject.top,
              width: drawingObject.width,
              height: drawingObject.height,
            },
            calculatedPercent: {
              leftPercent,
              topPercent,
              widthPercent,
              heightPercent,
            },
            timestamp: Date.now(),
          }
        );
      }

      // 新規作成時も枠線設定を統一
      drawingObject.set({
        strokeUniform: true,
        strokeDashArray: null,
        strokeLineCap: "butt",
        strokeLineJoin: "miter",
        strokeMiterLimit: 4,
        paintFirst: "fill",
      });

      // 単一オブジェクトから直接スタイル情報を取得
      const actualFill = drawingObject.fill;
      const actualStroke = drawingObject.stroke;
      const actualStrokeWidth = drawingObject.strokeWidth;

      // 描画データに追加（相対座標版、テキスト機能付き）
      const shortId =
        Date.now().toString(36) + Math.random().toString(36).substr(2, 3);
      let newDrawItem = [
        isEllipse ? "e" : "r", // type: ellipse or rectangle
        shortId,
        Math.round(leftPercent * 100) / 100, // 小数点2桁まで
        Math.round(topPercent * 100) / 100,
        Math.round(widthPercent * 100) / 100,
        Math.round(heightPercent * 100) / 100,
        actualFill !== "transparent" ? actualFill : null,
        actualStroke !== "#000000" ? actualStroke : null,
        actualStrokeWidth !== 2 ? actualStrokeWidth : null,
        drawingObject.opacity !== 1 ? drawingObject.opacity : null,
        0, // angle (回転角度)
        currentStyle.text || "", // テキスト内容
        currentStyle.textColor !== "#000000" ? currentStyle.textColor : null, // 文字色
        currentStyle.fontSize !== 16 ? currentStyle.fontSize : null, // フォントサイズ
        currentStyle.fontWeight !== 400 ? currentStyle.fontWeight : null, // フォントの太さ
        currentStyle.textAlignH !== "center" ? currentStyle.textAlignH : null, // 水平配置
        currentStyle.textAlignV !== "middle" ? currentStyle.textAlignV : null, // 垂直配置
        isEllipse
          ? null
          : currentStyle.cornerRadius !== 0
            ? currentStyle.cornerRadius
            : null, // 角の丸みは長方形のみ
      ];

      // 末尾のnullを削除してサイズを最小化
      while (
        newDrawItem.length > 6 &&
        newDrawItem[newDrawItem.length - 1] === null
      ) {
        newDrawItem.pop();
      }

      // テキストオブジェクトを作成（テキストが設定されている場合）
      let finalObject = drawingObject;

      if (currentStyle.text && currentStyle.text.trim()) {
        // グループ作成中フラグを設定（選択解除を防ぐため）
        window.drawLayerGroupCreating = true;

        // 新しいオブジェクトを作成（グループ用、枠線設定も含む）
        let newShapeObject;
        let shapeWidth, shapeHeight;

        if (isEllipse) {
          shapeWidth = drawingObject.rx * 2;
          shapeHeight = drawingObject.ry * 2;

          newShapeObject = new fabric.Ellipse({
            left: 0,
            top: 0,
            rx: drawingObject.rx,
            ry: drawingObject.ry,
            fill: drawingObject.fill,
            stroke: drawingObject.stroke,
            strokeWidth: drawingObject.strokeWidth,
            strokeUniform: true,
            strokeDashArray: null,
            strokeLineCap: "butt",
            strokeLineJoin: "miter",
            strokeMiterLimit: 4,
            paintFirst: "fill",
            opacity: drawingObject.opacity,
            selectable: false,
            evented: false,
          });
        } else {
          shapeWidth = drawingObject.width;
          shapeHeight = drawingObject.height;

          newShapeObject = new fabric.Rect({
            left: 0, // グループ内での相対位置
            top: 0, // グループ内での相対位置
            width: drawingObject.width,
            height: drawingObject.height,
            fill: drawingObject.fill,
            stroke: drawingObject.stroke,
            strokeWidth: drawingObject.strokeWidth,
            strokeUniform: true,
            strokeDashArray: null,
            strokeLineCap: "butt",
            strokeLineJoin: "miter",
            strokeMiterLimit: 4,
            paintFirst: "fill",
            opacity: drawingObject.opacity,
            selectable: false,
            evented: false,
          });
        }

        // テキストの配置を計算（グループ内での相対位置）
        const textLeft = calculateTextLeftForGroup(
          shapeWidth,
          currentStyle.textAlignH
        );
        const textTop = calculateTextTopForGroup(
          shapeHeight,
          currentStyle.textAlignV
        );

        const text = new fabric.Text(currentStyle.text, {
          left: textLeft,
          top: textTop,
          fontSize: currentStyle.fontSize,
          fill: currentStyle.textColor,
          fontWeight: currentStyle.fontWeight,
          textAlign: currentStyle.textAlignH,
          originX: getOriginX(currentStyle.textAlignH),
          originY: getOriginY(currentStyle.textAlignV),
          selectable: false,
          evented: false,
        });

        // 図形とテキストをグループ化
        const group = new fabric.Group([newShapeObject, text], {
          left: isEllipse ? drawingObject.left : drawingObject.left,
          top: isEllipse ? drawingObject.top : drawingObject.top,
          selectable: true,
          evented: true,
          // 選択時の表示制御
          hasControls: true,
          hasBorders: true,
          transparentCorners: false,
          cornerColor: "rgba(35, 151, 206, 0.8)",
          cornerStyle: "rect",
          cornerSize: 6,
          // グループ内オブジェクトの位置を固定
          subTargetCheck: false,
          interactive: false,
        });

        // グループ作成後に内部オブジェクトの位置を正規化
        const shapeInGroup = group._objects.find(
          (obj) => obj.type === "rect" || obj.type === "ellipse"
        );
        const textInGroup = group._objects.find((obj) => obj.type === "text");

        if (shapeInGroup) {
          shapeInGroup.set({
            left: 0,
            top: 0,
          });
        }

        if (textInGroup) {
          // テキスト位置を再計算
          const correctedTextLeft = calculateTextLeftForGroup(
            shapeWidth,
            currentStyle.textAlignH
          );
          const correctedTextTop = calculateTextTopForGroup(
            shapeHeight,
            currentStyle.textAlignV
          );

          textInGroup.set({
            left: correctedTextLeft,
            top: correctedTextTop,
          });
        }

        // 図形をキャンバスから削除してグループを追加
        canvas.remove(drawingObject);
        canvas.add(group);
        finalObject = group;

        // グループ作成完了後にフラグをクリア
        window.drawLayerGroupCreating = false;

        console.log("DrawLayer: Text added to new shape as group", {
          type: isEllipse ? "ellipse" : "rectangle",
          text: currentStyle.text,
          textPosition: { left: textLeft, top: textTop },
          textStyle: {
            fontSize: currentStyle.fontSize,
            fill: currentStyle.textColor,
            fontWeight: currentStyle.fontWeight,
            textAlign: currentStyle.textAlignH,
          },
        });
      }

      // オブジェクトのプロパティを更新
      const objectId = `draw_${shortId}`;
      finalObject.set({
        id: objectId,
        selectable: true,
        evented: true,
      });

      console.log("DrawLayer: Shape created with relative coordinates", {
        type: isEllipse ? "ellipse" : "rectangle",
        absolute: isEllipse
          ? {
              left: drawingObject.left,
              top: drawingObject.top,
              width: drawingObject.rx * 2,
              height: drawingObject.ry * 2,
            }
          : {
              left: drawingObject.left,
              top: drawingObject.top,
              width: drawingObject.width,
              height: drawingObject.height,
            },
        relative: { leftPercent, topPercent, widthPercent, heightPercent },
        canvasSize: actualSize,
        savedData: newDrawItem,
        hasText: !!(currentStyle.text && currentStyle.text.trim()),
      });

      // 選択されたオブジェクトのスタイルをcurrentStyleに反映（テキスト情報も含む）
      const objectStyle = {
        strokeWidth: drawingObject.strokeWidth || 2,
        stroke: drawingObject.stroke || "#000000",
        fill: drawingObject.fill || "transparent",
        opacity: drawingObject.opacity || 1,
        text: currentStyle.text || "",
        textColor: currentStyle.textColor || "#000000",
        fontSize: currentStyle.fontSize || 16,
        fontWeight: currentStyle.fontWeight || 400,
        textAlignH: currentStyle.textAlignH || "center",
        textAlignV: currentStyle.textAlignV || "middle",
      };
      setCurrentStyle(objectStyle);

      // 新しく作成されたオブジェクトを即座に選択
      canvas.setActiveObject(finalObject);
      canvas.renderAll();

      console.log("DrawLayer: NEW SHAPE CREATED - DETAILED LOG", {
        type: isEllipse ? "ellipse" : "rectangle",
        objectId: objectId,
        isSelected: !!canvas.getActiveObject(),
        selectedObjectId: canvas.getActiveObject()?.id,
        timestamp: Date.now(),
        action: "RECTANGLE_CREATED_AND_SELECTED",
      });

      // 保存前に最後に描画した長方形を選択するためのIDをグローバルに保存
      globalLastCreatedObjectId = objectId;
      selectionStateRef.current.lastCreatedObjectId = objectId;

      // オブジェクト作成完了を通知
      if (onObjectCreated) {
        onObjectCreated(objectId);
        console.log("DrawLayer: onObjectCreated called with", objectId);
      }

      // オブジェクト選択を通知
      if (onObjectSelected) {
        onObjectSelected(objectId);
        console.log("DrawLayer: onObjectSelected called with", objectId);
      }

      // 最後に保存（選択状態を維持するため）
      const updatedData = [...drawData, newDrawItem];
      if (onDrawDataChange) {
        onDrawDataChange(updatedData);
        console.log("DrawLayer: Shape saved after selection setup", {
          type: isEllipse ? "ellipse" : "rectangle",
          newDataLength: updatedData.length,
          savedObject: newDrawItem,
        });
      }
    }

    // 描画状態をクリア
    drawingStateRef.current.isDrawing = false;
    drawingStateRef.current.startPoint = null;
    drawingStateRef.current.drawingObject = null;
    drawingStateRef.current.sizeUpdatePaused = false; // サイズ更新の一時停止を解除
    drawingStateRef.current.recentlyCompleted = true; // 描画完了直後フラグを設定
    drawingStateRef.current.completionTimestamp = Date.now(); // 完了時刻を記録

    setIsDrawing(false);
    setStartPoint(null);
    // canvas.discardActiveObject(); // この行を削除して選択状態を維持
    canvas.renderAll();

    console.log("[DRAW_RESIZE:INFO] Size update resumed - drawing completed", {
      timestamp: Date.now(),
      recentlyCompleted: true,
      protectionDuration: "3000ms",
    });

    // 3秒後に保護フラグを解除
    setTimeout(() => {
      drawingStateRef.current.recentlyCompleted = false;
      drawingStateRef.current.completionTimestamp = null;
      console.log("[DRAW_RESIZE:INFO] Drawing completion protection expired", {
        timestamp: Date.now(),
      });
    }, 3000);
  };

  // テキスト配置計算関数
  const calculateTextLeft = (rect, alignH) => {
    const padding = 8; // 長方形の端からのパディング
    switch (alignH) {
      case "left":
        return rect.left + padding;
      case "center":
        return rect.left + rect.width / 2;
      case "right":
        return rect.left + rect.width - padding;
      default:
        return rect.left + rect.width / 2;
    }
  };

  const calculateTextTop = (rect, alignV) => {
    const padding = 8; // 長方形の端からのパディング
    switch (alignV) {
      case "top":
        return rect.top + padding;
      case "middle":
        return rect.top + rect.height / 2;
      case "bottom":
        return rect.top + rect.height - padding;
      default:
        return rect.top + rect.height / 2;
    }
  };

  const getOriginX = (alignH) => {
    switch (alignH) {
      case "left":
        return "left";
      case "center":
        return "center";
      case "right":
        return "right";
      default:
        return "center";
    }
  };

  const getOriginY = (alignV) => {
    switch (alignV) {
      case "top":
        return "top";
      case "middle":
        return "center";
      case "bottom":
        return "bottom";
      default:
        return "center";
    }
  };

  // グループオブジェクトのテキストを更新
  const updateGroupObjectText = (groupObject, newStyle, canvas) => {
    console.log(
      "[TEXT_LAYOUT_DEBUG] updateGroupObjectText called - BEFORE UPDATE:",
      JSON.stringify(
        {
          groupId: groupObject.id,
          newTextSettings: {
            text: newStyle.text,
            textAlignH: newStyle.textAlignH,
            textAlignV: newStyle.textAlignV,
            fontSize: newStyle.fontSize,
            textColor: newStyle.textColor,
          },
          groupStructureBefore: {
            groupPosition: { left: groupObject.left, top: groupObject.top },
            groupSize: { width: groupObject.width, height: groupObject.height },
            groupScale: {
              scaleX: groupObject.scaleX,
              scaleY: groupObject.scaleY,
            },
            objectsCount: groupObject._objects?.length,
            objects: groupObject._objects?.map((obj) => ({
              type: obj.type,
              position: { left: obj.left, top: obj.top },
              size: { width: obj.width, height: obj.height },
              text: obj.type === "text" ? obj.text : undefined,
              textAlign: obj.type === "text" ? obj.textAlign : undefined,
              originX: obj.type === "text" ? obj.originX : undefined,
              originY: obj.type === "text" ? obj.originY : undefined,
            })),
          },
          timestamp: Date.now(),
        },
        null,
        2
      )
    );

    if (!groupObject._objects || groupObject._objects.length === 0) {
      console.log("DrawLayer: Group has no objects");
      return;
    }

    // グループの位置を保存
    const savedGroupLeft = groupObject.left;
    const savedGroupTop = groupObject.top;

    // 図形とテキストオブジェクトを取得
    const rectObject = groupObject._objects.find((obj) => obj.type === "rect");
    const ellipseObject = groupObject._objects.find(
      (obj) => obj.type === "ellipse"
    );
    const shapeObject = rectObject || ellipseObject;
    let textObject = groupObject._objects.find((obj) => obj.type === "text");

    if (!shapeObject) {
      console.log("DrawLayer: No shape found in group");
      return;
    }

    // 図形の位置を保存
    const savedShapeLeft = shapeObject.left;
    const savedShapeTop = shapeObject.top;

    console.log("DrawLayer: Found objects in group", {
      shapeType: shapeObject.type,
      hasShape: !!shapeObject,
      hasText: !!textObject,
      newTextContent: newStyle.text,
      shapePosition: { left: shapeObject.left, top: shapeObject.top },
      shapeSize: ellipseObject
        ? { rx: ellipseObject.rx, ry: ellipseObject.ry }
        : { width: rectObject.width, height: rectObject.height },
    });

    // テキストが空の場合、テキストオブジェクトを削除
    if (!newStyle.text || !newStyle.text.trim()) {
      if (textObject) {
        console.log("DrawLayer: Removing text from group (empty text)");
        groupObject.removeWithUpdate(textObject);
        canvas.renderAll();
      }
      // 透明度を適用した色を計算
      const fillWithOpacity = applyOpacityToColor(
        newStyle.fill,
        newStyle.fillOpacity
      );
      const strokeWithOpacity = applyOpacityToColor(
        newStyle.stroke,
        newStyle.strokeOpacity
      );

      // 図形のスタイルは更新
      shapeObject.set({
        strokeWidth: newStyle.strokeWidth,
        stroke: strokeWithOpacity,
        fill: fillWithOpacity,
        opacity: newStyle.opacity,
      });

      // 長方形のみ角の丸みを適用
      if (rectObject) {
        rectObject.set({
          rx: newStyle.cornerRadius || 0,
          ry: newStyle.cornerRadius || 0,
        });
      }

      canvas.renderAll();
      return;
    }

    // テキストオブジェクトが存在する場合は更新
    if (textObject) {
      console.log("DrawLayer: Updating existing text object in group");

      // グループ内での相対位置を計算
      const shapeWidth = ellipseObject
        ? ellipseObject.rx * 2
        : rectObject.width;
      const shapeHeight = ellipseObject
        ? ellipseObject.ry * 2
        : rectObject.height;
      const baseTextLeft = calculateTextLeftForGroup(
        shapeWidth,
        newStyle.textAlignH
      );
      const baseTextTop = calculateTextTopForGroup(
        shapeHeight,
        newStyle.textAlignV
      );

      // 楕円の場合は中心基準、長方形の場合は左上基準
      let textLeft, textTop;
      if (ellipseObject) {
        // 楕円：中心からのオフセット
        textLeft = baseTextLeft - shapeWidth / 2;
        textTop = baseTextTop - shapeHeight / 2;
      } else {
        // 長方形：そのまま使用
        textLeft = baseTextLeft;
        textTop = baseTextTop;
      }

      console.log("DrawLayer: Calculated text position for group update", {
        shapeType: ellipseObject ? "ellipse" : "rectangle",
        basePosition: { left: baseTextLeft, top: baseTextTop },
        finalPosition: { left: textLeft, top: textTop },
        shapeSize: ellipseObject
          ? { rx: ellipseObject.rx, ry: ellipseObject.ry }
          : { width: rectObject.width, height: rectObject.height },
        alignH: newStyle.textAlignH,
        alignV: newStyle.textAlignV,
      });

      textObject.set({
        text: newStyle.text,
        left: textLeft,
        top: textTop,
        fontSize: newStyle.fontSize,
        fill: newStyle.textColor,
        fontWeight: newStyle.fontWeight,
        textAlign: newStyle.textAlignH,
        originX: getOriginX(newStyle.textAlignH),
        originY: getOriginY(newStyle.textAlignV),
      });

      console.log("DrawLayer: Text object updated in group", {
        text: newStyle.text,
        position: { left: textLeft, top: textTop },
        style: {
          fontSize: newStyle.fontSize,
          fill: newStyle.textColor,
          fontWeight: newStyle.fontWeight,
        },
      });
    } else {
      // テキストオブジェクトが存在しない場合は新規作成
      console.log("DrawLayer: Creating new text object for existing group");

      // グループ内での相対位置を計算
      const shapeWidth = ellipseObject
        ? ellipseObject.rx * 2
        : rectObject.width;
      const shapeHeight = ellipseObject
        ? ellipseObject.ry * 2
        : rectObject.height;
      const baseTextLeft = calculateTextLeftForGroup(
        shapeWidth,
        newStyle.textAlignH
      );
      const baseTextTop = calculateTextTopForGroup(
        shapeHeight,
        newStyle.textAlignV
      );

      // 楕円の場合は中心基準、長方形の場合は左上基準
      let textLeft, textTop;
      if (ellipseObject) {
        // 楕円：中心からのオフセット
        textLeft = baseTextLeft - shapeWidth / 2;
        textTop = baseTextTop - shapeHeight / 2;
      } else {
        // 長方形：そのまま使用
        textLeft = baseTextLeft;
        textTop = baseTextTop;
      }

      console.log("DrawLayer: Calculated text position for new text in group", {
        shapeType: ellipseObject ? "ellipse" : "rectangle",
        basePosition: { left: baseTextLeft, top: baseTextTop },
        finalPosition: { left: textLeft, top: textTop },
        shapeSize: ellipseObject
          ? { rx: ellipseObject.rx, ry: ellipseObject.ry }
          : { width: rectObject.width, height: rectObject.height },
      });

      const newTextObject = new fabric.Text(newStyle.text, {
        left: textLeft,
        top: textTop,
        fontSize: newStyle.fontSize,
        fill: newStyle.textColor,
        fontWeight: newStyle.fontWeight,
        textAlign: newStyle.textAlignH,
        originX: getOriginX(newStyle.textAlignH),
        originY: getOriginY(newStyle.textAlignV),
        selectable: false,
        evented: false,
      });

      console.log("DrawLayer: Adding new text object to group", {
        text: newStyle.text,
        textObject: newTextObject,
      });

      groupObject.addWithUpdate(newTextObject);

      console.log("DrawLayer: New text object added to group successfully");

      // addWithUpdate後に図形の位置を復元
      shapeObject.set({
        left: savedShapeLeft,
        top: savedShapeTop,
      });
      console.log(
        "[GROUP_POSITION_DEBUG] Shape position restored after addWithUpdate:",
        {
          restoredPosition: { left: savedShapeLeft, top: savedShapeTop },
        }
      );
    }

    // 図形のスタイルのみ更新（位置は変更しない）

    shapeObject.set({
      strokeWidth: newStyle.strokeWidth,
      stroke: newStyle.stroke,
      fill: newStyle.fill,
      opacity: newStyle.opacity,
      left: savedShapeLeft, // 位置を強制的に復元
      top: savedShapeTop, // 位置を強制的に復元
    });

    // 長方形のみ角の丸みを更新
    if (rectObject) {
      rectObject.set({
        rx: newStyle.cornerRadius || 0,
        ry: newStyle.cornerRadius || 0,
      });
    }

    console.log("[GROUP_POSITION_DEBUG] Shape position and style updated:", {
      restoredPosition: { left: savedShapeLeft, top: savedShapeTop },
      newStyle: {
        strokeWidth: newStyle.strokeWidth,
        stroke: newStyle.stroke,
        fill: newStyle.fill,
      },
    });

    // テキストオブジェクトの位置を図形の位置を考慮して計算
    if (textObject) {
      // 図形の実際の位置とサイズを考慮してテキスト位置を計算
      const shapeLeft = shapeObject.left;
      const shapeTop = shapeObject.top;
      const shapeWidth = ellipseObject
        ? ellipseObject.rx * 2
        : rectObject.width;
      const shapeHeight = ellipseObject
        ? ellipseObject.ry * 2
        : rectObject.height;

      // テキスト位置を図形の位置を基準に計算
      const baseTextLeft = calculateTextLeftForGroup(
        shapeWidth,
        newStyle.textAlignH
      );
      const baseTextTop = calculateTextTopForGroup(
        shapeHeight,
        newStyle.textAlignV
      );

      let correctedTextLeft, correctedTextTop;

      if (ellipseObject) {
        // 楕円の場合：中心基準なので、中心からのオフセットとして計算
        correctedTextLeft = baseTextLeft - shapeWidth / 2;
        correctedTextTop = baseTextTop - shapeHeight / 2;
      } else {
        // 長方形の場合：図形の位置オフセットを加算
        correctedTextLeft = shapeLeft + baseTextLeft;
        correctedTextTop = shapeTop + baseTextTop;
      }

      textObject.set({
        left: correctedTextLeft,
        top: correctedTextTop,
      });

      console.log("DrawLayer: Text position calculated", {
        shapeType: ellipseObject ? "ellipse" : "rectangle",
        shapePosition: { left: shapeLeft, top: shapeTop },
        shapeSize: { width: shapeWidth, height: shapeHeight },
        baseTextPosition: { left: baseTextLeft, top: baseTextTop },
        finalTextPosition: { left: correctedTextLeft, top: correctedTextTop },
        alignH: newStyle.textAlignH,
        alignV: newStyle.textAlignV,
      });
    }

    // グループの位置を復元（万が一変わっていたら）
    if (
      groupObject.left !== savedGroupLeft ||
      groupObject.top !== savedGroupTop
    ) {
      console.log("[GROUP_POSITION_DEBUG] Restoring group position:", {
        savedPosition: { left: savedGroupLeft, top: savedGroupTop },
        currentPosition: { left: groupObject.left, top: groupObject.top },
      });
      groupObject.set({
        left: savedGroupLeft,
        top: savedGroupTop,
      });
      groupObject.setCoords();
    }

    // グループを再描画
    canvas.renderAll();

    // renderAll後に図形の位置を再度確認して復元（Fabric.jsが位置をリセットする可能性があるため）
    if (ellipseObject) {
      const currentShapeLeft = shapeObject.left;
      const currentShapeTop = shapeObject.top;

      if (
        currentShapeLeft !== savedShapeLeft ||
        currentShapeTop !== savedShapeTop
      ) {
        console.log(
          "[GROUP_POSITION_DEBUG] Shape position changed after renderAll, forcing restoration:",
          {
            savedPosition: { left: savedShapeLeft, top: savedShapeTop },
            currentPosition: { left: currentShapeLeft, top: currentShapeTop },
          }
        );

        shapeObject.set({
          left: savedShapeLeft,
          top: savedShapeTop,
        });
        shapeObject.setCoords();

        // グループの座標も更新
        groupObject.setCoords();

        // 再描画
        canvas.renderAll();
      }
    }

    // 更新後のオブジェクト構造を詳細ログ
    const afterUpdateStructure = {
      groupId: groupObject.id,
      newTextSettings: {
        text: newStyle.text,
        textAlignH: newStyle.textAlignH,
        textAlignV: newStyle.textAlignV,
        fontSize: newStyle.fontSize,
        textColor: newStyle.textColor,
      },
      groupStructureAfter: {
        groupPosition: { left: groupObject.left, top: groupObject.top },
        groupSize: { width: groupObject.width, height: groupObject.height },
        groupScale: { scaleX: groupObject.scaleX, scaleY: groupObject.scaleY },
        objectsCount: groupObject._objects?.length,
        objects: groupObject._objects?.map((obj) => ({
          type: obj.type,
          position: { left: obj.left, top: obj.top },
          size: { width: obj.width, height: obj.height },
          text: obj.type === "text" ? obj.text : undefined,
          textAlign: obj.type === "text" ? obj.textAlign : undefined,
          originX: obj.type === "text" ? obj.originX : undefined,
          originY: obj.type === "text" ? obj.originY : undefined,
          fontSize: obj.type === "text" ? obj.fontSize : undefined,
          fill: obj.type === "text" ? obj.fill : obj.fill,
          actualRenderedPosition:
            obj.type === "text"
              ? {
                  calculatedLeft: obj.left,
                  calculatedTop: obj.top,
                  withOriginX: obj.originX,
                  withOriginY: obj.originY,
                  expectedLeft:
                    obj.type === "text"
                      ? calculateTextLeftForGroup(
                          groupObject._objects.find((o) => o.type === "rect")
                            ?.width || 0,
                          newStyle.textAlignH
                        )
                      : undefined,
                  expectedTop:
                    obj.type === "text"
                      ? calculateTextTopForGroup(
                          groupObject._objects.find((o) => o.type === "rect")
                            ?.height || 0,
                          newStyle.textAlignV
                        )
                      : undefined,
                }
              : undefined,
        })),
      },
      isSelected: !!canvas.getActiveObject(),
      selectedObjectId: canvas.getActiveObject()?.id,
      timestamp: Date.now(),
    };

    console.log(
      "[TEXT_LAYOUT_DEBUG] updateGroupObjectText called - AFTER UPDATE:",
      JSON.stringify(afterUpdateStructure, null, 2)
    );

    console.log("DrawLayer: Group object updated successfully with renderAll");
  };

  // 単一の図形（長方形または楕円）をテキスト付きグループに変換
  const convertShapeToGroupWithText = (shapeObject, newStyle, canvas) => {
    const isEllipse = shapeObject.type === "ellipse";

    console.log(
      "[GROUP_POSITION_DEBUG] BEFORE conversion - Original shape:",
      JSON.stringify(
        {
          shapeId: shapeObject.id,
          shapeType: shapeObject.type,
          position: { left: shapeObject.left, top: shapeObject.top },
          size: isEllipse
            ? { rx: shapeObject.rx, ry: shapeObject.ry }
            : { width: shapeObject.width, height: shapeObject.height },
          scale: { scaleX: shapeObject.scaleX, scaleY: shapeObject.scaleY },
          originX: shapeObject.originX,
          originY: shapeObject.originY,
        },
        null,
        2
      )
    );

    // テキストが空の場合は図形のスタイルのみ更新
    if (!newStyle.text || !newStyle.text.trim()) {
      console.log("DrawLayer: Empty text, updating shape style only");
      shapeObject.set({
        strokeWidth: newStyle.strokeWidth,
        stroke: newStyle.stroke,
        fill: newStyle.fill,
        opacity: newStyle.opacity,
      });
      canvas.renderAll();
      return;
    }

    // グループ作成中フラグを設定（選択解除を防ぐため）
    window.drawLayerGroupCreating = true;
    console.log(
      "DrawLayer: Set group creating flag to prevent selection clearing"
    );

    let shapeProps, newShapeObject, shapeWidth, shapeHeight;

    if (isEllipse) {
      // 楕円の場合：楕円はcenter基準なので特別な処理が必要
      const scaledRx = shapeObject.rx * (shapeObject.scaleX || 1);
      const scaledRy = shapeObject.ry * (shapeObject.scaleY || 1);
      shapeWidth = scaledRx * 2;
      shapeHeight = scaledRy * 2;

      shapeProps = {
        // 楕円の中心座標を保持
        centerLeft: shapeObject.left,
        centerTop: shapeObject.top,
        rx: scaledRx,
        ry: scaledRy,
        fill: shapeObject.fill || "transparent",
        stroke: shapeObject.stroke || "#000000",
        strokeWidth: shapeObject.strokeWidth || 2,
        opacity: shapeObject.opacity || 1,
        angle: shapeObject.angle || 0,
      };

      // 新しい楕円オブジェクトを作成（center基準を維持）
      newShapeObject = new fabric.Ellipse({
        left: 0,
        top: 0,
        rx: shapeProps.rx,
        ry: shapeProps.ry,
        originX: "center", // center基準を維持
        originY: "center", // center基準を維持
        fill: shapeProps.fill,
        stroke: shapeProps.stroke,
        strokeWidth: shapeProps.strokeWidth,
        strokeUniform: true,
        strokeDashArray: null,
        strokeLineCap: "butt",
        strokeLineJoin: "miter",
        strokeMiterLimit: 4,
        paintFirst: "fill",
        opacity: shapeProps.opacity,
        angle: 0,
        selectable: false,
        evented: false,
      });
    } else {
      // 長方形の場合
      shapeWidth = shapeObject.width * (shapeObject.scaleX || 1);
      shapeHeight = shapeObject.height * (shapeObject.scaleY || 1);

      shapeProps = {
        left: shapeObject.left,
        top: shapeObject.top,
        width: shapeWidth,
        height: shapeHeight,
        fill: shapeObject.fill || "transparent",
        stroke: shapeObject.stroke || "#000000",
        strokeWidth: shapeObject.strokeWidth || 2,
        opacity: shapeObject.opacity || 1,
        angle: shapeObject.angle || 0,
      };

      // 新しい長方形オブジェクトを作成
      newShapeObject = new fabric.Rect({
        left: 0,
        top: 0,
        width: shapeProps.width,
        height: shapeProps.height,
        fill: shapeProps.fill,
        stroke: shapeProps.stroke,
        strokeWidth: shapeProps.strokeWidth,
        opacity: shapeProps.opacity,
        angle: 0,
        selectable: false,
        evented: false,
      });
    }

    console.log(
      "[GROUP_POSITION_DEBUG] Shape props calculated:",
      JSON.stringify(
        {
          isEllipse,
          shapeProps,
          shapeSize: { width: shapeWidth, height: shapeHeight },
        },
        null,
        2
      )
    );

    // テキストオブジェクトを作成（グループ内での相対位置）
    // 楕円も長方形も、グループ内では同じように中心を0,0として配置
    let textLeft, textTop;
    if (isEllipse) {
      // 楕円の場合：グループ内の楕円は中心が0,0なので、そこから計算
      textLeft = 0;
      textTop = 0;
    } else {
      // 長方形の場合：左上基準
      textLeft = calculateTextLeftForGroup(shapeWidth, newStyle.textAlignH);
      textTop = calculateTextTopForGroup(shapeHeight, newStyle.textAlignV);
    }

    console.log("DrawLayer: Creating text with position", {
      textLeft,
      textTop,
      text: newStyle.text,
      fontSize: newStyle.fontSize,
      textColor: newStyle.textColor,
    });

    const textObject = new fabric.Text(newStyle.text, {
      left: textLeft,
      top: textTop,
      fontSize: newStyle.fontSize,
      fill: newStyle.textColor,
      fontWeight: newStyle.fontWeight,
      textAlign: newStyle.textAlignH,
      originX: getOriginX(newStyle.textAlignH),
      originY: getOriginY(newStyle.textAlignV),
      selectable: false,
      evented: false,
    });

    // グループを作成（楕円の場合は中心座標、グループ化後にバウンディングボックスで位置調整）
    const group = new fabric.Group([newShapeObject, textObject], {
      left: isEllipse ? shapeProps.centerLeft : shapeProps.left,
      top: isEllipse ? shapeProps.centerTop : shapeProps.top,
      selectable: true,
      evented: true,
    });

    // IDを保持
    group.set("id", shapeObject.id);

    // 楕円グループの場合、バウンディングボックスから実際の左上座標を取得して設定
    if (isEllipse) {
      const bounds = group.getBoundingRect();
      const ellipseTopLeft = {
        left: shapeProps.centerLeft - shapeProps.rx,
        top: shapeProps.centerTop - shapeProps.ry,
      };

      console.log(
        "[GROUP_POSITION_DEBUG] Ellipse group initial bounds:",
        JSON.stringify(
          {
            initialGroupPosition: { left: group.left, top: group.top },
            bounds: bounds,
            targetTopLeft: ellipseTopLeft,
          },
          null,
          2
        )
      );

      // グループ位置を楕円の左上座標に設定（バウンディングボックスの左上と一致させる）
      group.set({
        left: ellipseTopLeft.left,
        top: ellipseTopLeft.top,
      });
      group.setCoords();

      console.log(
        "[GROUP_POSITION_DEBUG] Ellipse group position set to top-left:",
        JSON.stringify(
          {
            newPosition: { left: ellipseTopLeft.left, top: ellipseTopLeft.top },
            newBounds: group.getBoundingRect(),
          },
          null,
          2
        )
      );
    }

    console.log(
      "[GROUP_POSITION_DEBUG] AFTER creation - Group created:",
      JSON.stringify(
        {
          groupId: group.id,
          groupPosition: { left: group.left, top: group.top },
          groupSize: { width: group.width, height: group.height },
          groupBounds: group.getBoundingRect(),
        },
        null,
        2
      )
    );

    console.log("DrawLayer: Starting group replacement process");

    try {
      console.log("DrawLayer: Step 1 - Removing old shape");
      // 古い図形を削除して新しいグループを追加
      canvas.remove(shapeObject);

      console.log("DrawLayer: Step 2 - Adding new group to canvas");
      canvas.add(group);

      console.log("DrawLayer: Step 3 - Setting group as active object");
      // グループを選択状態にする
      canvas.setActiveObject(group);

      console.log("DrawLayer: Step 4 - Rendering canvas");
      canvas.renderAll();

      const finalGroup = canvas.getActiveObject();
      console.log(
        "[GROUP_POSITION_DEBUG] FINAL state after replacement:",
        JSON.stringify(
          {
            activeObjectType: finalGroup?.type,
            activeObjectId: finalGroup?.id,
            finalGroupPosition: {
              left: finalGroup?.left,
              top: finalGroup?.top,
            },
            finalGroupSize: {
              width: finalGroup?.width,
              height: finalGroup?.height,
            },
            finalGroupBounds: finalGroup?.getBoundingRect(),
            canvasObjectCount: canvas.getObjects().length,
            success: true,
          },
          null,
          2
        )
      );
    } catch (error) {
      console.error(
        "DrawLayer: Error during group creation",
        JSON.stringify({
          errorMessage: error.message,
          errorStack: error.stack,
          step: "group_creation",
        })
      );

      // エラーが発生した場合は元の図形を復元
      try {
        canvas.add(shapeObject);
        canvas.setActiveObject(shapeObject);
        canvas.renderAll();
        console.log("DrawLayer: Restored original shape due to error");
      } catch (restoreError) {
        console.error(
          "DrawLayer: Error during shape restoration",
          restoreError
        );
      }
    } finally {
      // グループ作成完了後にフラグをクリア
      window.drawLayerGroupCreating = false;
      console.log("DrawLayer: Cleared group creating flag");
    }
  };

  // グループ内でのテキスト配置計算関数（選択時の位置ずれ修正版）
  const calculateTextLeftForGroup = (rectWidth, alignH) => {
    const padding = 8; // 長方形の端からのパディング
    let result;
    switch (alignH) {
      case "left":
        result = padding;
        break;
      case "center":
        result = rectWidth / 2;
        break;
      case "right":
        result = rectWidth - padding;
        break;
      default:
        result = rectWidth / 2;
        break;
    }

    console.log("[TEXT_POSITION_DEBUG] calculateTextLeftForGroup", {
      rectWidth,
      alignH,
      padding,
      result,
      timestamp: Date.now(),
    });

    return result;
  };

  const calculateTextTopForGroup = (rectHeight, alignV) => {
    const padding = 8; // 長方形の端からのパディング
    let result;
    switch (alignV) {
      case "top":
        result = padding;
        break;
      case "middle":
        result = rectHeight / 2;
        break;
      case "bottom":
        result = rectHeight - padding;
        break;
      default:
        result = rectHeight / 2;
        break;
    }

    console.log("[TEXT_POSITION_DEBUG] calculateTextTopForGroup", {
      rectHeight,
      alignV,
      padding,
      result,
      timestamp: Date.now(),
    });

    return result;
  };

  // ユニークIDの生成
  const generateId = () => {
    return `draw_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // 色に透明度を適用するヘルパー関数
  const applyOpacityToColor = (color, opacity) => {
    if (color === "transparent" || opacity === undefined || opacity === null) {
      return color;
    }

    // HEX色をRGBAに変換
    if (color.startsWith("#")) {
      const hex = color.slice(1);
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // RGB色の場合、既存の透明度を置き換え
    if (color.startsWith("rgb(")) {
      const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        const [, r, g, b] = rgbMatch;
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
    }

    // RGBA色の場合、透明度のみ置き換え
    if (color.startsWith("rgba(")) {
      const rgbaMatch = color.match(
        /rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/
      );
      if (rgbaMatch) {
        const [, r, g, b] = rgbaMatch;
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
    }

    return color;
  };

  // RGBA色から透明度を抽出するヘルパー関数
  const extractOpacityFromColor = (color) => {
    if (color === "transparent") {
      return 0;
    }

    if (color.startsWith("rgba(")) {
      const rgbaMatch = color.match(/rgba\([\d\s,]+,\s*([\d.]+)\)/);
      if (rgbaMatch) {
        return parseFloat(rgbaMatch[1]);
      }
    }

    return 1; // デフォルトは不透明
  };

  // フォントサイズの相対値変換関数
  const convertFontSizeToRelative = (absoluteFontSize, canvasHeight) => {
    // キャンバス高さに対する割合として保存（パーセンテージ）
    return (absoluteFontSize / canvasHeight) * 100;
  };

  const convertFontSizeToAbsolute = (relativeFontSize, canvasHeight) => {
    // 相対値から絶対値に変換
    return (relativeFontSize / 100) * canvasHeight;
  };

  // フォントサイズが相対値かどうかを判定
  const isFontSizeRelative = (fontSize) => {
    // 相対値は通常0.5-10%の範囲になる（絶対値は8-48px）
    return fontSize > 0 && fontSize < 15;
  };

  // データ形式を正規化（新旧両方に対応、テキスト機能付き）
  const normalizeDrawData = (item) => {
    // 配列形式（超最適化版）の場合
    if (Array.isArray(item)) {
      // 直線の場合
      if (item[0] === "l") {
        return {
          type: "line",
          id: `draw_${item[1]}`,
          x1: item[2] || 0,
          y1: item[3] || 0,
          x2: item[4] || 0,
          y2: item[5] || 0,
          stroke: item[6] || "#000000",
          strokeWidth: item[7] || 2,
          opacity: item[8] || 1,
          created: Date.now(),
          modified: Date.now(),
        };
      }

      // 長方形・楕円の場合
      return {
        type:
          item[0] === "r" ? "rectangle" : item[0] === "e" ? "ellipse" : item[0],
        id: `draw_${item[1]}`,
        x: item[2] || 0,
        y: item[3] || 0,
        width: item[4] || 0,
        height: item[5] || 0,
        fill: item[6] || "transparent",
        stroke: item[7] || "#000000",
        strokeWidth: item[8] || 2,
        opacity: item[9] || 1,
        angle: item[10] || 0, // 回転角度
        text: item[11] || "", // テキスト内容
        textColor: item[12] || "#000000", // 文字色
        fontSize: item[13] || 16, // フォントサイズ
        fontWeight: item[14] || 400, // フォントの太さ
        textAlignH: item[15] || "center", // 水平配置
        textAlignV: item[16] || "middle", // 垂直配置
        cornerRadius: item[17] || 0, // 角の丸みを追加
        created: Date.now(),
        modified: Date.now(),
      };
    }

    // オブジェクト形式（最適化版）の場合
    if (item.t && item.i) {
      return {
        type:
          item.t === "r" ? "rectangle" : item.t === "e" ? "ellipse" : item.t,
        id: item.i,
        x: item.x || 0,
        y: item.y || 0,
        width: item.w || 0,
        height: item.h || 0,
        fill: item.f || "transparent",
        stroke: item.s || "#000000",
        strokeWidth: item.sw || 2,
        opacity: item.o || 1,
        angle: item.a || 0, // 回転角度
        text: item.txt || "", // テキスト内容
        textColor: item.tc || "#000000", // 文字色
        fontSize: item.fs || 16, // フォントサイズ
        fontWeight: item.fw || 400, // フォントの太さ
        textAlignH: item.tah || "center", // 水平配置
        textAlignV: item.tav || "middle", // 垂直配置
        cornerRadius: item.cr || 0, // 角の丸みを追加
        created: item.c || Date.now(),
        modified: item.m || Date.now(),
      };
    }

    // 従来の形式の場合
    return {
      type: item.type || "rectangle",
      id: item.id,
      x: item.x || 0,
      y: item.y || 0,
      width: item.width || 0,
      height: item.height || 0,
      fill: item.fill || "transparent",
      stroke: item.stroke || "#000000",
      strokeWidth: item.strokeWidth || 2,
      opacity: item.opacity || 1,
      angle: item.angle || 0, // 回転角度
      text: item.text || "", // テキスト内容
      textColor: item.textColor || "#000000", // 文字色
      fontSize: item.fontSize || 16, // フォントサイズ
      fontWeight: item.fontWeight || 400, // フォントの太さ
      textAlignH: item.textAlignH || "center", // 水平配置
      textAlignV: item.textAlignV || "middle", // 垂直配置
      cornerRadius: item.cornerRadius || 0, // 角の丸みを追加
      created: item.created || Date.now(),
      modified: item.modified || Date.now(),
    };
  };

  // 現在のキャンバス状態を保存（相対座標版）- 修正版
  const saveCurrentCanvasState = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !onDrawDataChange) return;

    console.log(
      "[SAVE_DEBUG] saveCurrentCanvasState called - FIXED VERSION: " +
        JSON.stringify(
          {
            timestamp: Date.now(),
            reason: "Selection cleared or object modified",
            isUpdatingData: selectionStateRef.current.isUpdatingData,
            selectedObjectId: selectionStateRef.current.selectedObjectId,
            canvasObjectCount: canvas.getObjects().length,
          },
          null,
          2
        )
    );

    // キャンバスサイズを使用して相対座標変換（actualSizeではなくcanvasサイズを使用）
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    console.log("DrawLayer: Using canvas size for coordinate conversion", {
      canvasSize: { width: canvasWidth, height: canvasHeight },
      actualSizeState: actualSize,
      reason: "Prevent size shrinking by using fixed canvas size",
    });

    // キャンバス上のすべてのオブジェクトからデータを生成（相対座標版）
    const updatedData = canvas
      .getObjects()
      .map((obj) => {
        console.log(
          "[SAVE_DEBUG] Processing object: " +
            JSON.stringify(
              {
                id: obj.id,
                type: obj.type,
                hasId: !!obj.id,
              },
              null,
              2
            )
        );

        if (!obj.id) {
          console.log(
            "[SAVE_DEBUG] Object has no ID, skipping: " +
              JSON.stringify(
                {
                  type: obj.type,
                  timestamp: Date.now(),
                },
                null,
                2
              )
          );
          return null;
        }

        // Fabric.jsオブジェクトの現在の状態を取得（枠線の太さを補正）
        let currentLeft,
          currentTop,
          currentWidth,
          currentHeight,
          strokeWidth = 0;
        let objectType = "rectangle"; // デフォルトは長方形

        // グループオブジェクトの場合は図形の情報を取得
        if (obj.type === "group" && obj._objects) {
          const rectObject = obj._objects.find((o) => o.type === "rect");
          const ellipseObject = obj._objects.find((o) => o.type === "ellipse");
          const shapeObject = rectObject || ellipseObject;

          if (ellipseObject) {
            objectType = "ellipse";
            // 楕円グループの場合：グループ座標が中心、楕円内部も中心が0,0
            // グループの中心座標から半径を引いて左上座標を計算
            currentLeft = obj.left - ellipseObject.rx * obj.scaleX;
            currentTop = obj.top - ellipseObject.ry * obj.scaleY;
            currentWidth = ellipseObject.rx * 2 * obj.scaleX;
            currentHeight = ellipseObject.ry * 2 * obj.scaleY;
            strokeWidth = ellipseObject.strokeWidth || 0;

            // console.log('[GROUP_POSITION_DEBUG] Saving ellipse group - position conversion:', ...);
          } else if (rectObject) {
            // 位置の補正は不要、サイズのみ枠線の太さを補正
            strokeWidth = rectObject.strokeWidth || 0;
            currentLeft = obj.left;
            currentTop = obj.top;
            currentWidth = obj.width * obj.scaleX - strokeWidth;
            currentHeight = obj.height * obj.scaleY - strokeWidth;
          } else {
            currentLeft = obj.left;
            currentTop = obj.top;
            currentWidth = obj.width * obj.scaleX;
            currentHeight = obj.height * obj.scaleY;
          }
        } else if (obj.type === "ellipse") {
          // 単一楕円の場合：originX/Yが'center'なので中心から左上に変換
          objectType = "ellipse";
          currentLeft = obj.left - obj.rx * obj.scaleX;
          currentTop = obj.top - obj.ry * obj.scaleY;
          currentWidth = obj.rx * 2 * obj.scaleX;
          currentHeight = obj.ry * 2 * obj.scaleY;

          // console.log('[ELLIPSE_STYLE_DEBUG] Saving single ellipse:', obj.id);
        } else if (
          obj.type === "line" ||
          (obj.type === "path" && obj.isLinePath)
        ) {
          // 直線の場合（LineまたはPath直線）
          objectType = "line";

          let x1, y1, x2, y2;

          if (obj.type === "path" && obj.isLinePath) {
            // Path直線の場合：pathから座標を抽出（pathは既に絶対座標）
            const pathCommands = obj.path;
            if (pathCommands && pathCommands.length >= 2) {
              x1 = pathCommands[0][1];
              y1 = pathCommands[0][2];
              x2 = pathCommands[1][1];
              y2 = pathCommands[1][2];

              console.log("[PATH_LINE] Saving Path line", {
                id: obj.id,
                pathCommands: pathCommands,
                extractedCoords: { x1, y1, x2, y2 },
              });
            } else {
              console.error(
                "[PATH_LINE] Invalid path data for saving",
                pathCommands
              );
              return null; // 無効なパスはスキップ
            }
          } else {
            // Line直線の場合（後方互換性）
            x1 = obj.x1 + obj.left;
            y1 = obj.y1 + obj.top;
            x2 = obj.x2 + obj.left;
            y2 = obj.y2 + obj.top;
          }

          const x1Percent = (x1 / canvasWidth) * 100;
          const y1Percent = (y1 / canvasHeight) * 100;
          const x2Percent = (x2 / canvasWidth) * 100;
          const y2Percent = (y2 / canvasHeight) * 100;

          console.log(
            "[LINE_DEBUG] Saving line: " +
              JSON.stringify(
                {
                  id: obj.id,
                  type: obj.type,
                  isLinePath: obj.isLinePath,
                  raw: { x1, y1, x2, y2 },
                  calculated: { x1Percent, y1Percent, x2Percent, y2Percent },
                },
                null,
                2
              )
          );

          // 元の太さを取得（カスタムレンダリング用に保存した値）
          const actualStrokeWidth = obj._originalStrokeWidth || obj.strokeWidth;

          let lineData = [
            "l", // type: line
            obj.id.replace("draw_", "").replace(/_/g, ""),
            Math.round(x1Percent * 100) / 100,
            Math.round(y1Percent * 100) / 100,
            Math.round(x2Percent * 100) / 100,
            Math.round(y2Percent * 100) / 100,
            obj.stroke !== "#000000" ? obj.stroke : null,
            actualStrokeWidth !== 2 ? actualStrokeWidth : null,
            obj.opacity !== 1 ? obj.opacity : null,
          ];

          // 末尾のnullを削除
          while (
            lineData.length > 6 &&
            lineData[lineData.length - 1] === null
          ) {
            lineData.pop();
          }

          return lineData;
        } else {
          // 単一長方形の場合、枠線の太さ補正は不要（Fabric.jsが正しく処理）
          currentLeft = obj.left;
          currentTop = obj.top;
          currentWidth = obj.width * obj.scaleX;
          currentHeight = obj.height * obj.scaleY;
        }

        // 回転角度を正しく取得（グループの場合は内部オブジェクトから）
        let currentAngle = 0;
        if (obj.type === "group" && obj._objects) {
          const rectObject = obj._objects.find((o) => o.type === "rect");
          const textObject = obj._objects.find((o) => o.type === "text");
          currentAngle =
            rectObject?.angle || textObject?.angle || obj.angle || 0;
        } else {
          currentAngle = obj.angle || 0;
        }

        // 相対座標に変換（パーセンテージ）- キャンバスサイズを使用
        const leftPercent = (currentLeft / canvasWidth) * 100;
        const topPercent = (currentTop / canvasHeight) * 100;
        const widthPercent = (currentWidth / canvasWidth) * 100;
        const heightPercent = (currentHeight / canvasHeight) * 100;

        // 楕円保存ログ（必要に応じてコメント解除）
        // if (objectType === 'ellipse') {
        //   console.log('[ELLIPSE_SAVE_DEBUG]', obj.id, 'w:', currentWidth, 'h:', currentHeight);
        // }

        // テキスト情報を抽出（グループオブジェクトの場合）
        let textInfo = {
          text: "",
          textColor: "#000000",
          fontSize: 16,
          fontWeight: 400,
          textAlignH: "center",
          textAlignV: "middle",
        };

        if (obj.type === "group" && obj._objects) {
          const textObject = obj._objects.find((o) => o.type === "text");
          if (textObject) {
            textInfo = {
              text: textObject.text || "",
              textColor: textObject.fill || "#000000",
              fontSize: textObject.fontSize || 16,
              fontWeight: textObject.fontWeight || 400,
              textAlignH: textObject.textAlign || "center",
              textAlignV:
                textObject.originY === "top"
                  ? "top"
                  : textObject.originY === "bottom"
                    ? "bottom"
                    : "middle",
            };

            console.log("DrawLayer: Extracting text info from group object", {
              objectId: obj.id,
              extractedText: textInfo.text,
              textColor: textInfo.textColor,
              fontSize: textInfo.fontSize,
              textAlign: textInfo.textAlignH,
            });
          }
        }

        // 相対座標で配列形式で保存してサイズを最小化（テキスト情報も含む）
        let optimizedData = [
          objectType === "ellipse" ? "e" : "r", // type: ellipse or rectangle
          obj.id
            .replace("draw_", "")
            .replace(/_/g, ""), // IDを短縮
          Math.round(leftPercent * 100) / 100, // 小数点2桁まで
          Math.round(topPercent * 100) / 100,
          Math.round(widthPercent * 100) / 100,
          Math.round(heightPercent * 100) / 100,
        ];

        // スタイル情報を追加（デフォルト値以外のみ）
        const rectObject =
          obj.type === "group"
            ? obj._objects?.find((o) => o.type === "rect")
            : obj;
        const ellipseObjectForStyle =
          obj.type === "group"
            ? obj._objects?.find((o) => o.type === "ellipse")
            : obj.type === "ellipse"
              ? obj
              : null;
        const shapeObjectForStyle = rectObject || ellipseObjectForStyle || obj;
        const fill = shapeObjectForStyle?.fill || obj.fill;
        const stroke = shapeObjectForStyle?.stroke || obj.stroke;
        const objStrokeWidth =
          shapeObjectForStyle?.strokeWidth || obj.strokeWidth;
        const opacity = shapeObjectForStyle?.opacity || obj.opacity;

        // デフォルト値以外のみ追加
        if (fill && fill !== "transparent") {
          optimizedData.push(fill);
        } else {
          optimizedData.push(null);
        }

        if (stroke && stroke !== "#000000") {
          optimizedData.push(stroke);
        } else {
          optimizedData.push(null);
        }

        if (objStrokeWidth && objStrokeWidth !== 2) {
          optimizedData.push(objStrokeWidth);
        } else {
          optimizedData.push(null);
        }

        if (opacity && opacity !== 1) {
          optimizedData.push(opacity);
        } else {
          optimizedData.push(null);
        }

        if (currentAngle !== 0) {
          optimizedData.push(Math.round(currentAngle * 100) / 100);
        } else {
          optimizedData.push(null);
        }

        // テキスト情報を追加（フォントサイズは相対値に変換）
        optimizedData.push(textInfo.text || null); // テキスト内容
        optimizedData.push(
          textInfo.textColor !== "#000000" ? textInfo.textColor : null
        ); // 文字色

        // フォントサイズを相対値に変換して保存
        let fontSizeToSave = null;
        if (textInfo.fontSize && textInfo.fontSize !== 16) {
          const relativeFontSize = convertFontSizeToRelative(
            textInfo.fontSize,
            canvasHeight
          );
          fontSizeToSave = Math.round(relativeFontSize * 100) / 100; // 小数点2桁まで
          console.log(
            "DrawLayer: Converting absolute font size to relative for save",
            {
              absoluteFontSize: textInfo.fontSize,
              relativeFontSize: fontSizeToSave,
              canvasHeight: canvasHeight,
            }
          );
        }
        optimizedData.push(fontSizeToSave); // フォントサイズ（相対値）

        optimizedData.push(
          textInfo.fontWeight !== 400 ? textInfo.fontWeight : null
        ); // フォントの太さ
        optimizedData.push(
          textInfo.textAlignH !== "center" ? textInfo.textAlignH : null
        ); // 水平配置
        optimizedData.push(
          textInfo.textAlignV !== "middle" ? textInfo.textAlignV : null
        ); // 垂直配置

        // 角の丸み情報を追加（長方形のみ）
        const cornerRadius =
          objectType === "rectangle" ? rectObject?.rx || obj.rx || 0 : 0;
        optimizedData.push(
          objectType === "rectangle" && cornerRadius !== 0 ? cornerRadius : null
        ); // 角の丸み

        // 末尾のnullを削除してサイズを最小化
        while (
          optimizedData.length > 6 &&
          optimizedData[optimizedData.length - 1] === null
        ) {
          optimizedData.pop();
        }

        console.log(
          "[SAVE_DEBUG] Optimized data created: " +
            JSON.stringify(
              {
                objectId: obj.id,
                objectType: objectType,
                dataLength: optimizedData.length,
                data: optimizedData,
                timestamp: Date.now(),
              },
              null,
              2
            )
        );

        return optimizedData;
      })
      .filter((item) => item !== null);

    console.log(
      "[SAVE_DEBUG] Saving canvas state with canvas-based coordinates: " +
        JSON.stringify(
          {
            objectCount: updatedData.length,
            dataSize: JSON.stringify(updatedData).length,
            dataPreview: updatedData.slice(0, 3),
            usedCanvasSize: { width: canvasWidth, height: canvasHeight },
            timestamp: Date.now(),
          },
          null,
          2
        )
    );

    onDrawDataChange(updatedData);

    console.log(
      "[SAVE_DEBUG] onDrawDataChange called successfully: " +
        JSON.stringify(
          {
            updatedDataLength: updatedData.length,
            timestamp: Date.now(),
          },
          null,
          2
        )
    );
  };

  // スタイル変更ハンドラー（完全に修正版）
  const handleStyleChange = (newStyle) => {
    console.log("DrawLayer: handleStyleChange called", {
      newStyle,
      currentStyle,
      hasTextChange: newStyle.text !== currentStyle.text,
      hasFillOpacityChange: newStyle.fillOpacity !== currentStyle.fillOpacity,
      hasStrokeOpacityChange:
        newStyle.strokeOpacity !== currentStyle.strokeOpacity,
      timestamp: Date.now(),
    });

    // 重複実行を防ぐため、グループ作成中またはスタイル更新中は処理をスキップ
    if (window.drawLayerGroupCreating || window.drawLayerUpdatingStyle) {
      console.log(
        "DrawLayer: Skipping style change during group creation or style update",
        {
          groupCreating: !!window.drawLayerGroupCreating,
          updatingStyle: !!window.drawLayerUpdatingStyle,
        }
      );
      return;
    }

    // まずcurrentStyleを更新（これによりUIが更新される）
    setCurrentStyle(newStyle);

    // 選択されているオブジェクトがあれば、そのスタイルも更新
    const canvas = fabricCanvasRef.current;
    if (!canvas) {
      console.log("DrawLayer: No canvas available");
      return;
    }

    const activeObject = canvas.getActiveObject();
    if (!activeObject || !activeObject.id) {
      console.log("DrawLayer: No active object to apply style to");
      return;
    }

    console.log("DrawLayer: Updating active object style", {
      objectId: activeObject.id,
      objectType: activeObject.type,
      isGroup: activeObject.type === "group",
    });

    // 透明度変更フラグを確認
    const hasOpacityChange =
      newStyle.fillOpacity !== currentStyle.fillOpacity ||
      newStyle.strokeOpacity !== currentStyle.strokeOpacity;

    // スタイル更新中フラグを設定（無限ループを防ぐ）
    window.drawLayerUpdatingStyle = true;

    try {
      // テキスト関連の変更がある場合の処理
      if (
        newStyle.text !== currentStyle.text ||
        newStyle.textColor !== currentStyle.textColor ||
        newStyle.fontSize !== currentStyle.fontSize ||
        newStyle.fontWeight !== currentStyle.fontWeight ||
        newStyle.textAlignH !== currentStyle.textAlignH ||
        newStyle.textAlignV !== currentStyle.textAlignV
      ) {
        console.log(
          "DrawLayer: Text-related changes detected, updating object"
        );

        // グループオブジェクトの場合、テキストを更新または追加
        if (activeObject.type === "group") {
          console.log(
            "[OBJECT_STRUCTURE_DEBUG] Updating existing group object with text"
          );

          // 選択時のオブジェクト構造を詳細ログ
          console.log(
            "[OBJECT_STRUCTURE_DEBUG] Selected group object structure",
            {
              groupType: activeObject.type,
              groupId: activeObject.id,
              groupPosition: { left: activeObject.left, top: activeObject.top },
              groupSize: {
                width: activeObject.width,
                height: activeObject.height,
              },
              groupScale: {
                scaleX: activeObject.scaleX,
                scaleY: activeObject.scaleY,
              },
              objectsCount: activeObject._objects?.length,
              objects: activeObject._objects?.map((obj) => ({
                type: obj.type,
                position: { left: obj.left, top: obj.top },
                size: { width: obj.width, height: obj.height },
                text: obj.type === "text" ? obj.text : undefined,
                textAlign: obj.type === "text" ? obj.textAlign : undefined,
                originX: obj.type === "text" ? obj.originX : undefined,
                originY: obj.type === "text" ? obj.originY : undefined,
              })),
              timestamp: Date.now(),
            }
          );

          updateGroupObjectText(activeObject, newStyle, canvas);
        } else {
          console.log(
            "[OBJECT_STRUCTURE_DEBUG] Converting single shape to group with text"
          );

          // 単一オブジェクトの構造を詳細ログ
          console.log(
            "[OBJECT_STRUCTURE_DEBUG] Single object structure before conversion",
            {
              objectType: activeObject.type,
              objectId: activeObject.id,
              position: { left: activeObject.left, top: activeObject.top },
              size:
                activeObject.type === "ellipse"
                  ? { rx: activeObject.rx, ry: activeObject.ry }
                  : { width: activeObject.width, height: activeObject.height },
              scale: {
                scaleX: activeObject.scaleX,
                scaleY: activeObject.scaleY,
              },
              timestamp: Date.now(),
            }
          );

          convertShapeToGroupWithText(activeObject, newStyle, canvas);
        }
      } else {
        // 通常のスタイル更新（長方形のスタイルのみ）
        console.log("DrawLayer: Updating non-text styles only");

        if (activeObject.type === "group" && activeObject._objects) {
          const rectObject = activeObject._objects.find(
            (obj) => obj.type === "rect"
          );
          const ellipseObject = activeObject._objects.find(
            (obj) => obj.type === "ellipse"
          );
          const shapeObject = rectObject || ellipseObject;

          if (shapeObject) {
            // 楕円のスタイル変更ログ（必要に応じてコメント解除）
            // if (ellipseObject) {
            //   console.log('[ELLIPSE_STYLE_DEBUG] Before:', JSON.stringify({
            //     rx: ellipseObject.rx, ry: ellipseObject.ry, strokeWidth: ellipseObject.strokeWidth
            //   }));
            // }

            // 透明度を適用した色を計算
            const fillWithOpacity = applyOpacityToColor(
              newStyle.fill,
              newStyle.fillOpacity
            );
            const strokeWithOpacity = applyOpacityToColor(
              newStyle.stroke,
              newStyle.strokeOpacity
            );

            shapeObject.set({
              strokeWidth: newStyle.strokeWidth,
              stroke: strokeWithOpacity,
              fill: fillWithOpacity,
              opacity: newStyle.opacity,
            });

            // 長方形のみ角の丸みを適用
            if (rectObject) {
              rectObject.set({
                rx: newStyle.cornerRadius || 0,
                ry: newStyle.cornerRadius || 0,
              });
            }

            // if (ellipseObject) {
            //   console.log('[ELLIPSE_STYLE_DEBUG] After:', JSON.stringify({
            //     rx: ellipseObject.rx, ry: ellipseObject.ry, strokeWidth: ellipseObject.strokeWidth
            //   }));
            // }
          }

          // グループ全体に回転角度を適用
          activeObject.set({
            angle: newStyle.angle !== undefined ? newStyle.angle : 0,
          });
          console.log("DrawLayer: Applied rotation to group object");
        } else {
          // 透明度を適用した色を計算
          const fillWithOpacity = applyOpacityToColor(
            newStyle.fill,
            newStyle.fillOpacity
          );
          const strokeWithOpacity = applyOpacityToColor(
            newStyle.stroke,
            newStyle.strokeOpacity
          );

          // オブジェクトタイプに応じて処理
          if (activeObject.type === "ellipse") {
            // 楕円の場合：rx/ryは変更しない
            activeObject.set({
              strokeWidth: newStyle.strokeWidth,
              stroke: strokeWithOpacity,
              fill: fillWithOpacity,
              opacity: newStyle.opacity,
              angle: newStyle.angle !== undefined ? newStyle.angle : 0,
            });
            // console.log('[ELLIPSE_STYLE_DEBUG] Single ellipse style updated');
          } else if (
            activeObject.type === "line" ||
            (activeObject.type === "path" && activeObject.isLinePath)
          ) {
            // 直線の場合（LineまたはPath直線）：線の太さ、色、透明度のみ
            activeObject.set({
              strokeWidth: newStyle.strokeWidth,
              stroke: strokeWithOpacity,
              opacity: newStyle.opacity,
            });
            console.log(
              "[LINE_STYLE] Applied style changes to line: " +
                JSON.stringify(
                  {
                    lineId: activeObject.id,
                    lineType: activeObject.type,
                    isLinePath: activeObject.isLinePath,
                    strokeWidth: newStyle.strokeWidth,
                    stroke: strokeWithOpacity,
                    opacity: newStyle.opacity,
                  },
                  null,
                  2
                )
            );
          } else {
            // 長方形の場合：角の丸みを適用
            activeObject.set({
              strokeWidth: newStyle.strokeWidth,
              stroke: strokeWithOpacity,
              fill: fillWithOpacity,
              opacity: newStyle.opacity,
              angle: newStyle.angle !== undefined ? newStyle.angle : 0,
              rx: newStyle.cornerRadius || 0,
              ry: newStyle.cornerRadius || 0,
            });
            console.log(
              "DrawLayer: Applied non-text style changes to single rectangle"
            );
          }
        }

        // レンダリング
        canvas.renderAll();
      }

      console.log("DrawLayer: Style update completed successfully");

      // 透明度変更時は視覚的な更新のみ（保存はスライダーのonMouseUp/onTouchEndで行う）
      if (hasOpacityChange) {
        console.log(
          "DrawLayer: Opacity changed - visual update only, save on mouse/touch end",
          {
            fillOpacity: newStyle.fillOpacity,
            strokeOpacity: newStyle.strokeOpacity,
            objectId: activeObject.id,
            timestamp: Date.now(),
          }
        );
      }
    } catch (error) {
      console.error("DrawLayer: Error during style update", error);
    } finally {
      // フラグをクリア
      setTimeout(() => {
        window.drawLayerUpdatingStyle = false;
      }, 100);
    }
  };

  // 選択クリア時のハンドラー
  const handleSelectionCleared = (event) => {
    console.log("DrawLayer: SELECTION CLEARED", {
      timestamp: Date.now(),
      hasPendingNewObject: !!selectionStateRef.current.pendingNewObject,
      eventTarget: event?.e?.target?.tagName,
    });

    // スタイル変更中は選択解除を無視
    if (event && event.e) {
      const target = event.e.target;
      if (target) {
        const isStylePanelElement =
          target.closest("[data-style-panel]") ||
          target.closest("input") ||
          target.closest("button") ||
          target.closest("label") ||
          target.closest("span") ||
          target.hasAttribute("data-style-panel") ||
          target.tagName === "INPUT" ||
          target.tagName === "BUTTON" ||
          target.tagName === "LABEL";

        if (isStylePanelElement) {
          console.log(
            "DrawLayer: Selection cleared by style panel interaction, ignoring",
            {
              tagName: target.tagName,
              className: target.className,
              hasDataStylePanel: target.hasAttribute("data-style-panel"),
              closestStylePanel: !!target.closest("[data-style-panel]"),
            }
          );
          return;
        }
      }
    }

    // 保留中の新しいオブジェクトがある場合は保存
    if (selectionStateRef.current.pendingNewObject) {
      const pendingObject = selectionStateRef.current.pendingNewObject;
      console.log("DrawLayer: Saving pending new object on selection clear", {
        objectId: pendingObject.objectId,
        drawData: pendingObject.drawData,
        timestamp: pendingObject.timestamp,
      });

      const updatedData = [...drawData, pendingObject.drawData];
      if (onDrawDataChange) {
        onDrawDataChange(updatedData);
        console.log("DrawLayer: Pending object saved to data", {
          newDataLength: updatedData.length,
          savedObject: pendingObject.drawData,
        });
      }

      // 保留状態をクリア
      selectionStateRef.current.pendingNewObject = null;
    } else {
      // 通常の選択解除時にデータを保存
      saveCurrentCanvasState();
      console.log(
        "DrawLayer: Normal selection clear - saved current canvas state"
      );
    }

    // isUpdatingDataフラグをクリア（遅延実行でloadDrawDataの再作成を防止）
    if (selectionStateRef.current.isUpdatingData) {
      console.log(
        "DrawLayer: Scheduling isUpdatingData flag clear after data save"
      );
      // 200ms後にフラグをクリア（データ保存→loadDrawData呼び出しの後に実行されるようにする）
      setTimeout(() => {
        selectionStateRef.current.isUpdatingData = false;
        selectionStateRef.current.selectedObjectId = null;
        console.log("DrawLayer: Cleared isUpdatingData flag (delayed)", {
          timestamp: Date.now(),
        });
      }, 200);
    }

    // オブジェクト選択解除を通知
    if (onObjectDeselected) {
      onObjectDeselected();
    }

    // デフォルトスタイルに戻す
    const defaultStyle = {
      strokeWidth: 2,
      stroke: "#000000",
      fill: "transparent",
      opacity: 1,
    };

    setCurrentStyle(defaultStyle);

    console.log(
      "DrawLayer: Selection cleared, style reset to default, data saved"
    );
  };

  // オブジェクト移動中のハンドラー（直線用 - 移動開始時のpath保存のみ）
  const handleObjectMoving = (event) => {
    const movingObject = event.target;
    if (
      movingObject &&
      movingObject.type === "path" &&
      movingObject.isLinePath
    ) {
      // コントロールポイント編集中かチェック
      const isControlPointEdit =
        event.transform?.corner &&
        (event.transform.corner === "startPoint" ||
          event.transform.corner === "endPoint");

      if (isControlPointEdit) {
        // コントロールポイント編集中は何もしない（actionHandlerで処理）
        console.log(
          "[LINE_MOVE_NEW] Control point edit detected, skipping: " +
            JSON.stringify(
              {
                lineId: movingObject.id,
                corner: event.transform.corner,
                timestamp: Date.now(),
              },
              null,
              2
            )
        );
        return;
      }

      // 初回移動時のみ：元のpath座標を保存（Fabric.jsの移動は許可）
      if (!movingObject._originalPath) {
        movingObject._originalPath = {
          x1: movingObject.path[0][1],
          y1: movingObject.path[0][2],
          x2: movingObject.path[1][1],
          y2: movingObject.path[1][2],
        };
        console.log(
          "[LINE_MOVE_NEW] Movement started, saving original path: " +
            JSON.stringify(
              {
                lineId: movingObject.id,
                originalPath: movingObject._originalPath,
                currentLeftTop: {
                  left: movingObject.left,
                  top: movingObject.top,
                },
                timestamp: Date.now(),
              },
              null,
              2
            )
        );
      }

      // Fabric.jsのデフォルト移動を許可（left/topが変化する）
      console.log(
        "[LINE_MOVE_NEW] Moving (Fabric.js default): " +
          JSON.stringify(
            {
              lineId: movingObject.id,
              currentLeftTop: {
                left: movingObject.left,
                top: movingObject.top,
              },
              timestamp: Date.now(),
            },
            null,
            2
          )
      );
    }
  };

  // オブジェクト変形時のハンドラー
  const handleObjectModified = (event) => {
    const modifiedObject = event.target;
    if (modifiedObject && modifiedObject.id) {
      console.log("[LINE_DEBUG] Object modified", {
        type: modifiedObject.type,
        id: modifiedObject.id,
        beforeModification: {
          left: modifiedObject.left,
          top: modifiedObject.top,
          scaleX: modifiedObject.scaleX,
          scaleY: modifiedObject.scaleY,
        },
      });

      console.log(
        "[RESIZE_DEBUG] Object modification started:",
        JSON.stringify(
          {
            objectId: modifiedObject.id,
            objectType: modifiedObject.type,
            beforeModification: {
              position: { left: modifiedObject.left, top: modifiedObject.top },
              size: {
                width: modifiedObject.width,
                height: modifiedObject.height,
              },
              scale: {
                scaleX: modifiedObject.scaleX,
                scaleY: modifiedObject.scaleY,
              },
              angle: modifiedObject.angle,
            },
            isGroup: modifiedObject.type === "group",
            hasObjects: modifiedObject._objects?.length || 0,
            timestamp: Date.now(),
          },
          null,
          2
        )
      );

      // グループオブジェクトの場合の特別な処理
      if (modifiedObject.type === "group" && modifiedObject._objects) {
        const rectObject = modifiedObject._objects.find(
          (obj) => obj.type === "rect"
        );
        const ellipseObject = modifiedObject._objects.find(
          (obj) => obj.type === "ellipse"
        );
        const shapeObject = rectObject || ellipseObject;
        const textObject = modifiedObject._objects.find(
          (obj) => obj.type === "text"
        );

        // 現在の回転角度を保存（内部オブジェクトから取得）
        const currentRotation =
          shapeObject?.angle || textObject?.angle || modifiedObject.angle || 0;

        console.log(
          "[RESIZE_DEBUG] Group object details before processing:",
          JSON.stringify(
            {
              groupScale: {
                scaleX: modifiedObject.scaleX,
                scaleY: modifiedObject.scaleY,
              },
              currentRotation: currentRotation,
              shapeType: shapeObject?.type,
              shapeObject: shapeObject
                ? {
                    position: { left: shapeObject.left, top: shapeObject.top },
                    size: ellipseObject
                      ? { rx: ellipseObject.rx, ry: ellipseObject.ry }
                      : {
                          width: shapeObject.width,
                          height: shapeObject.height,
                        },
                    scale: {
                      scaleX: shapeObject.scaleX,
                      scaleY: shapeObject.scaleY,
                    },
                    angle: shapeObject.angle,
                  }
                : null,
              textObject: textObject
                ? {
                    position: { left: textObject.left, top: textObject.top },
                    text: textObject.text,
                    align: {
                      textAlign: textObject.textAlign,
                      originX: textObject.originX,
                      originY: textObject.originY,
                    },
                    angle: textObject.angle,
                  }
                : null,
              timestamp: Date.now(),
            },
            null,
            2
          )
        );

        // 新しいサイズを計算（スコープ外でも使用するため外側で宣言）
        let newShapeWidth = modifiedObject.width;
        let newShapeHeight = modifiedObject.height;

        if (
          shapeObject &&
          (modifiedObject.scaleX !== 1 || modifiedObject.scaleY !== 1)
        ) {
          // グループのスケールを図形に適用
          if (ellipseObject) {
            // 楕円の場合：スケールを独立して適用（縦横比を変更可能）
            newShapeWidth = ellipseObject.rx * 2 * modifiedObject.scaleX;
            newShapeHeight = ellipseObject.ry * 2 * modifiedObject.scaleY;
          } else {
            // 長方形の場合
            newShapeWidth = shapeObject.width * modifiedObject.scaleX;
            newShapeHeight = shapeObject.height * modifiedObject.scaleY;
          }

          console.log(
            "[RESIZE_DEBUG] Applying group scale to shape:",
            JSON.stringify(
              {
                shapeType: shapeObject.type,
                oldSize: ellipseObject
                  ? { rx: ellipseObject.rx, ry: ellipseObject.ry }
                  : { width: shapeObject.width, height: shapeObject.height },
                newSize: ellipseObject
                  ? { rx: newShapeWidth / 2, ry: newShapeHeight / 2 }
                  : { width: newShapeWidth, height: newShapeHeight },
                groupScale: {
                  scaleX: modifiedObject.scaleX,
                  scaleY: modifiedObject.scaleY,
                },
                preservedRotation: currentRotation,
                timestamp: Date.now(),
              },
              null,
              2
            )
          );

          // 図形のサイズと位置を更新（回転角度を保持）
          if (ellipseObject) {
            ellipseObject.set({
              left: 0,
              top: 0,
              rx: newShapeWidth / 2,
              ry: newShapeHeight / 2,
              scaleX: 1,
              scaleY: 1,
              angle: currentRotation,
            });
          } else if (rectObject) {
            rectObject.set({
              left: -newShapeWidth / 2,
              top: -newShapeHeight / 2,
              width: newShapeWidth,
              height: newShapeHeight,
              scaleX: 1,
              scaleY: 1,
              angle: currentRotation,
            });
          }

          // テキストの位置を再計算（図形の新しい位置を考慮）
          if (textObject) {
            const textAlignH = textObject.textAlign || "center";
            const textAlignV =
              textObject.originY === "top"
                ? "top"
                : textObject.originY === "bottom"
                  ? "bottom"
                  : "middle";

            // 図形が中央配置されているため、テキスト位置も調整
            const newTextLeft =
              calculateTextLeftForGroup(newShapeWidth, textAlignH) -
              (ellipseObject ? 0 : newShapeWidth / 2);
            const newTextTop =
              calculateTextTopForGroup(newShapeHeight, textAlignV) -
              (ellipseObject ? 0 : newShapeHeight / 2);

            console.log(
              "[RESIZE_DEBUG] Updating text position after resize:",
              JSON.stringify(
                {
                  oldTextPosition: {
                    left: textObject.left,
                    top: textObject.top,
                  },
                  newTextPosition: { left: newTextLeft, top: newTextTop },
                  calculatedTextLeft: calculateTextLeftForGroup(
                    newShapeWidth,
                    textAlignH
                  ),
                  calculatedTextTop: calculateTextTopForGroup(
                    newShapeHeight,
                    textAlignV
                  ),
                  textAlign: { h: textAlignH, v: textAlignV },
                  newShapeSize: {
                    width: newShapeWidth,
                    height: newShapeHeight,
                  },
                  shapePosition: ellipseObject
                    ? { left: 0, top: 0 }
                    : { left: -newShapeWidth / 2, top: -newShapeHeight / 2 },
                  shapeType: shapeObject.type,
                  preservedRotation: currentRotation,
                  timestamp: Date.now(),
                },
                null,
                2
              )
            );

            // テキスト位置を強制的に更新
            textObject.set({
              left: newTextLeft,
              top: newTextTop,
              angle: currentRotation, // テキストも回転角度を保持
            });

            // 座標を強制的に更新
            textObject.setCoords();

            console.log("[RESIZE_DEBUG] Text position forcibly updated:", {
              finalTextPosition: { left: textObject.left, top: textObject.top },
              expectedPosition: { left: newTextLeft, top: newTextTop },
              positionMatches:
                textObject.left === newTextLeft &&
                textObject.top === newTextTop,
              timestamp: Date.now(),
            });
          }

          // 内部オブジェクトの座標を更新
          shapeObject.setCoords();
          if (textObject) {
            textObject.setCoords();
          }

          // グループのサイズとスケールを更新（位置と回転は変更しない）
          modifiedObject.set({
            width: newShapeWidth,
            height: newShapeHeight,
            scaleX: 1,
            scaleY: 1,
            angle: 0, // グループ自体の回転は常に0（内部オブジェクトで管理）
          });

          // グループの座標を更新（位置は変更せずに内部構造のみ更新）
          modifiedObject.setCoords();

          // リサイズ完了処理（自動再選択は削除）
          console.log(
            "[RESIZE_DEBUG] Resize completed - user can deselect to fix display",
            {
              objectId: modifiedObject.id,
              timestamp: Date.now(),
              note: "User should deselect and reselect to fix text position",
            }
          );

          console.log(
            "[RESIZE_DEBUG] Group size and scale updated with rotation preserved:",
            JSON.stringify(
              {
                groupId: modifiedObject.id,
                positionKept: {
                  left: modifiedObject.left,
                  top: modifiedObject.top,
                },
                newGroupSize: { width: newShapeWidth, height: newShapeHeight },
                finalScale: { scaleX: 1, scaleY: 1 },
                shapeSizeUpdated: ellipseObject
                  ? { rx: newShapeWidth / 2, ry: newShapeHeight / 2 }
                  : { width: newShapeWidth, height: newShapeHeight },
                rotationPreserved: currentRotation,
                shapeAngle: shapeObject?.angle,
                textAngle: textObject?.angle,
                shapeType: shapeObject.type,
                timestamp: Date.now(),
              },
              null,
              2
            )
          );
        }
      } else if (modifiedObject.type === "path" && modifiedObject.isLinePath) {
        // Path直線の場合：最終的なleft/topの反映とクリーンアップ
        console.log(
          "[LINE_MODIFIED] Path line modified - final update: " +
            JSON.stringify(
              {
                lineId: modifiedObject.id,
                left: modifiedObject.left,
                top: modifiedObject.top,
                hasOriginalPath: !!modifiedObject._originalPath,
                timestamp: Date.now(),
              },
              null,
              2
            )
        );

        // 残っているleft/topをpathに反映
        if (
          modifiedObject._originalPath &&
          (modifiedObject.left !== 0 || modifiedObject.top !== 0)
        ) {
          const originalPath = modifiedObject._originalPath;
          const finalX1 = originalPath.x1 + modifiedObject.left;
          const finalY1 = originalPath.y1 + modifiedObject.top;
          const finalX2 = originalPath.x2 + modifiedObject.left;
          const finalY2 = originalPath.y2 + modifiedObject.top;

          const finalPathData = `M ${finalX1} ${finalY1} L ${finalX2} ${finalY2}`;
          modifiedObject.set("path", fabric.util.parsePath(finalPathData));

          console.log(
            "[LINE_MODIFIED] Applied final offset to path: " +
              JSON.stringify(
                {
                  lineId: modifiedObject.id,
                  finalOffset: {
                    left: modifiedObject.left,
                    top: modifiedObject.top,
                  },
                  finalPath: {
                    x1: finalX1,
                    y1: finalY1,
                    x2: finalX2,
                    y2: finalY2,
                  },
                  timestamp: Date.now(),
                },
                null,
                2
              )
          );
        }

        // クリーンアップ
        delete modifiedObject._originalPath;

        // left/topを0にリセット
        modifiedObject.set({ left: 0, top: 0 });
        modifiedObject.setCoords();

        // 選択状態を維持するためにselectedObjectIdを保存
        selectionStateRef.current.isUpdatingData = true;
        selectionStateRef.current.selectedObjectId = modifiedObject.id;

        // Path直線の移動/編集完了後は即座に保存
        saveCurrentCanvasState();
      } else if (modifiedObject.type === "ellipse") {
        // 単一楕円の場合：scaleをrx/ryに変換
        if (modifiedObject.scaleX !== 1 || modifiedObject.scaleY !== 1) {
          const newRx = modifiedObject.rx * modifiedObject.scaleX;
          const newRy = modifiedObject.ry * modifiedObject.scaleY;

          console.log(
            "[RESIZE_DEBUG] Single ellipse scale reset:",
            JSON.stringify(
              {
                objectId: modifiedObject.id,
                objectType: modifiedObject.type,
                oldRadius: { rx: modifiedObject.rx, ry: modifiedObject.ry },
                newRadius: { rx: newRx, ry: newRy },
                scale: {
                  scaleX: modifiedObject.scaleX,
                  scaleY: modifiedObject.scaleY,
                },
                timestamp: Date.now(),
              },
              null,
              2
            )
          );

          modifiedObject.set({
            rx: newRx,
            ry: newRy,
            scaleX: 1,
            scaleY: 1,
          });
        }
      } else if (modifiedObject.type === "line") {
        // 直線の場合：scale/left/topを座標に統合（1回のみ実行）
        if (
          !selectionStateRef.current.lineScaleResetProcessed[
            modifiedObject.id
          ] &&
          (modifiedObject.scaleX !== 1 ||
            modifiedObject.scaleY !== 1 ||
            modifiedObject.left !== 0 ||
            modifiedObject.top !== 0)
        ) {
          // 処理済みフラグを設定
          selectionStateRef.current.lineScaleResetProcessed[modifiedObject.id] =
            true;

          const newX1 =
            modifiedObject.x1 * modifiedObject.scaleX + modifiedObject.left;
          const newY1 =
            modifiedObject.y1 * modifiedObject.scaleY + modifiedObject.top;
          const newX2 =
            modifiedObject.x2 * modifiedObject.scaleX + modifiedObject.left;
          const newY2 =
            modifiedObject.y2 * modifiedObject.scaleY + modifiedObject.top;

          console.log("[LINE_DEBUG] Before scale/offset reset", {
            objectId: modifiedObject.id,
            before: {
              x1: modifiedObject.x1,
              y1: modifiedObject.y1,
              x2: modifiedObject.x2,
              y2: modifiedObject.y2,
              left: modifiedObject.left,
              top: modifiedObject.top,
              scaleX: modifiedObject.scaleX,
              scaleY: modifiedObject.scaleY,
            },
            after: { x1: newX1, y1: newY1, x2: newX2, y2: newY2 },
          });

          modifiedObject.set({
            x1: newX1,
            y1: newY1,
            x2: newX2,
            y2: newY2,
            left: 0,
            top: 0,
            scaleX: 1,
            scaleY: 1,
          });
          modifiedObject.setCoords();

          console.log("[LINE_RESIZE_FIX] Line scale/offset reset (one-time)", {
            objectId: modifiedObject.id,
            newCoordinates: { x1: newX1, y1: newY1, x2: newX2, y2: newY2 },
          });

          // 正しい座標を保存（強制復元用）
          selectionStateRef.current.lineSavedCoordinates[modifiedObject.id] = {
            x1: newX1,
            y1: newY1,
            x2: newX2,
            y2: newY2,
            timestamp: Date.now(),
          };

          console.log(
            "[LINE_RESTORE] Saved line coordinates for force restoration",
            {
              objectId: modifiedObject.id,
              savedCoordinates:
                selectionStateRef.current.lineSavedCoordinates[
                  modifiedObject.id
                ],
            }
          );

          // 100ms, 300ms, 500msの複数回チェックで座標を強制復元
          [100, 300, 500].forEach((delay) => {
            setTimeout(() => {
              const canvas = fabricCanvasRef.current;
              if (!canvas || canvas._disposed) return;

              const lineObj = canvas
                .getObjects()
                .find((obj) => obj.id === modifiedObject.id);
              if (!lineObj || lineObj.type !== "line") return;

              const savedCoords =
                selectionStateRef.current.lineSavedCoordinates[
                  modifiedObject.id
                ];
              if (!savedCoords) return;

              // 復元前の状態を詳細に記録
              const beforeRestore = {
                x1: lineObj.x1,
                y1: lineObj.y1,
                x2: lineObj.x2,
                y2: lineObj.y2,
                left: lineObj.left,
                top: lineObj.top,
                scaleX: lineObj.scaleX,
                scaleY: lineObj.scaleY,
                getBoundingRect: lineObj.getBoundingRect(),
              };

              console.log(
                "[LINE_RESTORE] BEFORE restore at " +
                  delay +
                  "ms: " +
                  JSON.stringify(
                    {
                      objectId: modifiedObject.id,
                      before: beforeRestore,
                      savedCoords: savedCoords,
                      willRestore: true,
                    },
                    null,
                    2
                  )
              );

              lineObj.set({
                x1: savedCoords.x1,
                y1: savedCoords.y1,
                x2: savedCoords.x2,
                y2: savedCoords.y2,
                left: 0,
                top: 0,
                scaleX: 1,
                scaleY: 1,
              });
              lineObj.setCoords();
              lineObj.dirty = true;
              canvas.requestRenderAll();

              // 復元後の状態を詳細に記録
              const afterRestore = {
                x1: lineObj.x1,
                y1: lineObj.y1,
                x2: lineObj.x2,
                y2: lineObj.y2,
                left: lineObj.left,
                top: lineObj.top,
                scaleX: lineObj.scaleX,
                scaleY: lineObj.scaleY,
                getBoundingRect: lineObj.getBoundingRect(),
              };

              console.log(
                "[LINE_RESTORE] AFTER restore at " +
                  delay +
                  "ms: " +
                  JSON.stringify(
                    {
                      objectId: modifiedObject.id,
                      after: afterRestore,
                      changed: {
                        x1: afterRestore.x1 !== beforeRestore.x1,
                        y1: afterRestore.y1 !== beforeRestore.y1,
                        x2: afterRestore.x2 !== beforeRestore.x2,
                        y2: afterRestore.y2 !== beforeRestore.y2,
                      },
                      actuallyRestored:
                        afterRestore.x1 === savedCoords.x1 &&
                        afterRestore.y1 === savedCoords.y1 &&
                        afterRestore.x2 === savedCoords.x2 &&
                        afterRestore.y2 === savedCoords.y2,
                    },
                    null,
                    2
                  )
              );

              // 最後のチェック時にクリーンアップ
              if (delay === 500) {
                delete selectionStateRef.current.lineScaleResetProcessed[
                  modifiedObject.id
                ];
              }
            }, delay);
          });
        }
      } else {
        // 単一長方形の場合の従来の処理
        if (modifiedObject.scaleX !== 1 || modifiedObject.scaleY !== 1) {
          const newWidth = modifiedObject.width * modifiedObject.scaleX;
          const newHeight = modifiedObject.height * modifiedObject.scaleY;

          console.log(
            "[RESIZE_DEBUG] Single rectangle scale reset:",
            JSON.stringify(
              {
                objectId: modifiedObject.id,
                oldSize: {
                  width: modifiedObject.width,
                  height: modifiedObject.height,
                },
                newSize: { width: newWidth, height: newHeight },
                scale: {
                  scaleX: modifiedObject.scaleX,
                  scaleY: modifiedObject.scaleY,
                },
                timestamp: Date.now(),
              },
              null,
              2
            )
          );

          modifiedObject.set({
            width: newWidth,
            height: newHeight,
            scaleX: 1,
            scaleY: 1,
          });
        }
      }

      // キャンバスを再描画
      const canvas = fabricCanvasRef.current;

      // 選択状態を維持したまま枠線表示を修正
      if (canvas.getActiveObject() === modifiedObject) {
        // オブジェクトの座標を更新
        modifiedObject.setCoords();
        // 選択状態を強制的に更新
        canvas.setActiveObject(modifiedObject);
        console.log("[RESIZE_DEBUG] Object coordinates and selection updated");
      }

      canvas.renderAll();

      // 直線の場合、変更後の座標をログ出力して確認
      if (modifiedObject.type === "line") {
        console.log(
          "[LINE_RESIZE_ISSUE] Line coordinates after modification (scale reset completed):",
          JSON.stringify(
            {
              objectId: modifiedObject.id,
              coordinates: {
                x1: modifiedObject.x1,
                y1: modifiedObject.y1,
                x2: modifiedObject.x2,
                y2: modifiedObject.y2,
              },
              position: { left: modifiedObject.left, top: modifiedObject.top },
              size: {
                width: modifiedObject.width,
                height: modifiedObject.height,
              },
              scale: {
                scaleX: modifiedObject.scaleX,
                scaleY: modifiedObject.scaleY,
              },
              timestamp: Date.now(),
              note: "Scale has been reset to 1, data will be saved on selection clear",
            },
            null,
            2
          )
        );

        // 直線も他の図形と同じように、選択解除時に保存する
        // （handleObjectModifiedでは保存しない）
      }

      console.log(
        "[RESIZE_DEBUG] Object modification completed and rendered:",
        JSON.stringify(
          {
            objectId: modifiedObject.id,
            objectType: modifiedObject.type,
            afterModification: {
              position: { left: modifiedObject.left, top: modifiedObject.top },
              size: {
                width: modifiedObject.width,
                height: modifiedObject.height,
              },
              scale: {
                scaleX: modifiedObject.scaleX,
                scaleY: modifiedObject.scaleY,
              },
              angle: modifiedObject.angle,
            },
            selectionRefreshed: true,
            renderCompleted: true,
            timestamp: Date.now(),
          },
          null,
          2
        )
      );
    }
  };

  // 選択したオブジェクトを削除
  const handleDeleteSelected = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject && activeObject.id) {
      // キャンバスからオブジェクトを削除
      canvas.remove(activeObject);
      canvas.renderAll();

      // データからも削除（最適化版対応）
      const updatedData = drawData.filter((item) => {
        const normalizedItem = normalizeDrawData(item);
        return normalizedItem.id !== activeObject.id;
      });
      if (onDrawDataChange) {
        onDrawDataChange(updatedData);
      }
      console.log("DrawLayer: Object deleted via button", activeObject.id);
    }
  };

  // 選択解除関数をrefに設定
  const clearSelection = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) {
      console.log("DrawLayer: No canvas for clear selection");
      return;
    }

    console.log("DrawLayer: Clearing selection manually");
    canvas.discardActiveObject();
    canvas.renderAll();

    // スタイルをデフォルトに戻す
    const defaultStyle = {
      strokeWidth: 2,
      stroke: "#000000",
      fill: "transparent",
      opacity: 1,
    };
    setCurrentStyle(defaultStyle);

    // オブジェクト選択解除を通知
    if (onObjectDeselected) {
      onObjectDeselected();
    }

    console.log("DrawLayer: Selection cleared manually");
  }, [onObjectDeselected]);

  // clearDrawSelectionRefに選択解除関数を設定
  useEffect(() => {
    if (clearDrawSelectionRef) {
      clearDrawSelectionRef.current = clearSelection;
    }
  }, [clearSelection, clearDrawSelectionRef]);

  // リサイズ完了後の再選択処理（削除）
  // ユーザーが手動で選択解除→再選択することで正しい表示になる仕様に変更

  return (
    <>
      {/* DrawLayerを直接配置（Portalを使わずに正しい位置に配置） */}
      <DrawLayerContainer
        ref={containerRef}
        id="mirai-draw-layer"
        className={`draw-layer-container ${isDrawingMode ? "drawing-mode" : ""}`}
        data-drawing-mode={isDrawingMode}
        data-drawing-tool={drawingTool}
        isPrintMode={isPrintMode}
      >
        <canvas
          ref={canvasRef}
          id="mirai-draw-canvas"
          className={`draw-canvas ${isDrawingMode ? "drawing-mode" : ""}`}
          data-testid="draw-canvas"
          style={{
            cursor: isDrawingMode ? "crosshair" : "default",
            pointerEvents: "auto", // Canvas表示時は常にクリック可能（既存長方形選択のため）
            width: "100%",
            height: "100%",
          }}
        />
      </DrawLayerContainer>

      {showStylePanel && (
        <DrawToolbarPortal
          isVisible={showStylePanel}
          onStyleChange={handleStyleChange}
          onDeleteSelected={handleDeleteSelected}
          onSaveCanvas={saveCurrentCanvasState}
          currentStyle={currentStyle}
        />
      )}
    </>
  );
};

// 別途定義したReactベースのDrawToolbarコンポーネント
const DrawToolbar = ({
  isVisible,
  onStyleChange,
  onDeleteSelected,
  onSaveCanvas,
  currentStyle,
}) => {
  const [isComposing, setIsComposing] = useState(false);
  const [tempText, setTempText] = useState("");

  // currentStyleのテキストが変更された時にtempTextをクリア
  useEffect(() => {
    if (!isComposing) {
      setTempText("");
    }
  }, [currentStyle.text, isComposing]);

  if (!isVisible) return null;

  const handleStrokeWidth = (e) => {
    const newStyle = { ...currentStyle, strokeWidth: parseInt(e.target.value) };
    onStyleChange(newStyle);
  };

  const handleStrokeWidthEnd = () => {
    console.log("DrawLayer: Stroke width slider released, saving state");
    if (onSaveCanvas) {
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
      }
      onSaveCanvas();
    }
  };

  const handleStroke = (e) => {
    const newStyle = { ...currentStyle, stroke: e.target.value };
    onStyleChange(newStyle);
  };

  const handleFill = (e) => {
    // 色を設定したときは透明度も1（不透明）に設定
    const newStyle = {
      ...currentStyle,
      fill: e.target.value,
      fillOpacity: 1, // 透明度を不透明に設定
    };
    onStyleChange(newStyle);

    // 色変更時も保存
    setTimeout(() => {
      if (onSaveCanvas) {
        const canvas = window.fabricCanvasRef?.current;
        const activeObject = canvas?.getActiveObject();
        if (activeObject && activeObject.id && window.selectionStateRef) {
          window.selectionStateRef.current.isUpdatingData = true;
          window.selectionStateRef.current.selectedObjectId = activeObject.id;
        }
        onSaveCanvas();
      }
    }, 0);
  };

  const handleFillTransparent = () => {
    const newStyle = { ...currentStyle, fill: "transparent" };
    onStyleChange(newStyle);

    // 透明ボタンクリック時も保存
    setTimeout(() => {
      if (onSaveCanvas) {
        const canvas = window.fabricCanvasRef?.current;
        const activeObject = canvas?.getActiveObject();
        if (activeObject && activeObject.id && window.selectionStateRef) {
          window.selectionStateRef.current.isUpdatingData = true;
          window.selectionStateRef.current.selectedObjectId = activeObject.id;
        }
        onSaveCanvas();
      }
    }, 0);
  };

  const handleOpacity = (e) => {
    // スライダーの値を反転（0% = 不透明、100% = 透明）
    const sliderValue = parseFloat(e.target.value);
    const actualOpacity = 1 - sliderValue; // 反転
    const newStyle = { ...currentStyle, opacity: actualOpacity };
    onStyleChange(newStyle);
  };

  const handleOpacityEnd = () => {
    console.log("DrawLayer: Opacity slider released, saving state");
    if (onSaveCanvas) {
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
      }
      onSaveCanvas();
    }
  };

  const handleFillOpacity = (e) => {
    // 全体透明度と同じ実装：スライダーの値を反転（0 = 不透明、1 = 透明）
    const sliderValue = parseFloat(e.target.value);
    const actualOpacity = 1 - sliderValue; // 反転
    const newStyle = { ...currentStyle, fillOpacity: actualOpacity };
    onStyleChange(newStyle);
  };

  const handleFillOpacityEnd = () => {
    // マウス/タッチリリース時に保存（選択状態を維持）
    console.log(
      "DrawLayer: Fill opacity slider released, saving state with selection preserved"
    );
    if (onSaveCanvas) {
      // 選択状態をrefに保存（loadDrawDataで自動復元）
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
        console.log("DrawLayer: Set selection state ref for restoration", {
          objectId: activeObject.id,
          isUpdatingData: true,
        });
      }
      onSaveCanvas();
    }
  };

  const handleStrokeOpacity = (e) => {
    // 全体透明度と同じ実装：スライダーの値を反転（0 = 不透明、1 = 透明）
    const sliderValue = parseFloat(e.target.value);
    const actualOpacity = 1 - sliderValue; // 反転
    const newStyle = { ...currentStyle, strokeOpacity: actualOpacity };
    onStyleChange(newStyle);
  };

  const handleStrokeOpacityEnd = () => {
    // マウス/タッチリリース時に保存（選択状態を維持）
    console.log(
      "DrawLayer: Stroke opacity slider released, saving state with selection preserved"
    );
    if (onSaveCanvas) {
      // 選択状態をrefに保存（loadDrawDataで自動復元）
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
        console.log("DrawLayer: Set selection state ref for restoration", {
          objectId: activeObject.id,
          isUpdatingData: true,
        });
      }
      onSaveCanvas();
    }
  };

  // 日本語入力対応のテキストハンドラー
  const handleTextCompositionStart = (e) => {
    console.log("[TEXT_IME_DEBUG] Composition started", {
      currentValue: e.target.value,
      currentStyleText: currentStyle.text,
    });
    setIsComposing(true);
    // 日本語変換開始時に現在のテキストをtempTextに設定
    setTempText(e.target.value);
  };

  const handleTextCompositionEnd = (e) => {
    console.log("[TEXT_IME_DEBUG] Composition ended", {
      inputValue: e.target.value,
      timestamp: Date.now(),
    });
    setIsComposing(false);

    // 日本語変換確定時にスタイルを更新
    const newStyle = { ...currentStyle, text: e.target.value };
    onStyleChange(newStyle);
  };

  const handleTextChange = (e) => {
    const inputValue = e.target.value;

    console.log("[TEXT_IME_DEBUG] Text change", {
      inputValue,
      isComposing,
      timestamp: Date.now(),
    });

    // 日本語変換中は一時的にローカル状態のみ更新
    if (isComposing) {
      setTempText(inputValue);
      return;
    }

    // 日本語変換中でない場合は通常通り更新
    const newStyle = { ...currentStyle, text: inputValue };
    onStyleChange(newStyle);
  };

  // 表示用のテキスト値を決定
  const displayText = isComposing ? tempText : currentStyle.text || "";

  const handleTextColor = (e) => {
    const newStyle = { ...currentStyle, textColor: e.target.value };
    onStyleChange(newStyle);
  };

  const handleFontSize = (e) => {
    const newStyle = { ...currentStyle, fontSize: parseInt(e.target.value) };
    onStyleChange(newStyle);
  };

  const handleFontSizeEnd = () => {
    console.log("DrawLayer: Font size slider released, saving state");
    if (onSaveCanvas) {
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
      }
      onSaveCanvas();
    }
  };

  const handleFontWeight = (e) => {
    const newStyle = { ...currentStyle, fontWeight: parseInt(e.target.value) };
    onStyleChange(newStyle);
  };

  const handleFontWeightEnd = () => {
    console.log("DrawLayer: Font weight slider released, saving state");
    if (onSaveCanvas) {
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
      }
      onSaveCanvas();
    }
  };

  const handleTextAlignH = (value) => {
    const newStyle = { ...currentStyle, textAlignH: value };
    onStyleChange(newStyle);
  };

  const handleTextAlignV = (value) => {
    const newStyle = { ...currentStyle, textAlignV: value };
    onStyleChange(newStyle);
  };

  const handleAngle = (e) => {
    const newStyle = { ...currentStyle, angle: parseInt(e.target.value) };
    onStyleChange(newStyle);
  };

  const handleAngleEnd = () => {
    console.log("DrawLayer: Angle slider released, saving state");
    if (onSaveCanvas) {
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
      }
      onSaveCanvas();
    }
  };

  const handleCornerRadius = (e) => {
    const newStyle = {
      ...currentStyle,
      cornerRadius: parseInt(e.target.value),
    };
    onStyleChange(newStyle);
  };

  const handleCornerRadiusEnd = () => {
    console.log("DrawLayer: Corner radius slider released, saving state");
    if (onSaveCanvas) {
      const canvas = window.fabricCanvasRef?.current;
      const activeObject = canvas?.getActiveObject();
      if (activeObject && activeObject.id && window.selectionStateRef) {
        window.selectionStateRef.current.isUpdatingData = true;
        window.selectionStateRef.current.selectedObjectId = activeObject.id;
      }
      onSaveCanvas();
    }
  };

  const toolbarStyle = {
    position: "fixed",
    top: "120px",
    right: "20px",
    background: "white",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "15px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 10000,
    minWidth: "280px",
    fontSize: "14px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    maxHeight: "80vh",
    overflowY: "auto",
  };

  const labelStyle = {
    minWidth: "80px",
    fontWeight: 500,
    color: "#555",
  };

  const inputStyle = {
    flex: 1,
    padding: "6px 8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "13px",
  };

  const buttonStyle = {
    flex: 1,
    padding: "6px 8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
  };

  // 選択されているオブジェクトのタイプを判定
  const isEllipse = currentStyle.shapeType === "ellipse";
  const shapeLabel = isEllipse ? "楕円形" : "長方形";

  return (
    <div style={toolbarStyle} data-style-panel="true">
      <h3
        style={{
          margin: "0 0 15px 0",
          fontSize: "16px",
          color: "#333",
          borderBottom: "1px solid #eee",
          paddingBottom: "8px",
        }}
      >
        {shapeLabel}スタイル設定
      </h3>

      {/* 長方形スタイル設定 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>枠線の太さ:</label>
        <input
          type="range"
          min="1"
          max="10"
          value={currentStyle.strokeWidth}
          onChange={handleStrokeWidth}
          onMouseUp={handleStrokeWidthEnd}
          onTouchEnd={handleStrokeWidthEnd}
          style={{
            flex: 1,
            height: "6px",
            background: "#ddd",
            borderRadius: "3px",
            outline: "none",
          }}
        />
        <span
          style={{
            minWidth: "40px",
            textAlign: "right",
            fontWeight: 500,
            color: "#666",
          }}
        >
          {currentStyle.strokeWidth}px
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>枠線の色:</label>
        <input
          type="color"
          value={currentStyle.stroke}
          onChange={handleStroke}
          style={{
            width: "40px",
            height: "30px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
            padding: 0,
          }}
        />
        <span
          style={{ fontFamily: "monospace", fontSize: "12px", color: "#666" }}
        >
          {currentStyle.stroke}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>中身の色:</label>
        <input
          type="color"
          value={
            currentStyle.fill === "transparent" ? "#ffffff" : currentStyle.fill
          }
          onChange={handleFill}
          style={{
            width: "40px",
            height: "30px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
            padding: 0,
          }}
        />
        <button
          onClick={handleFillTransparent}
          style={{
            padding: "4px 8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            background:
              currentStyle.fill === "transparent" ? "#e9ecef" : "white",
            cursor: "pointer",
            fontSize: "12px",
          }}
        >
          透明
        </button>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>中身の透明度:</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={1 - (currentStyle.fillOpacity ?? 1)}
          onChange={handleFillOpacity}
          onMouseUp={handleFillOpacityEnd}
          onTouchEnd={handleFillOpacityEnd}
          style={{
            flex: 1,
            height: "6px",
            background: "#ddd",
            borderRadius: "3px",
            outline: "none",
          }}
        />
        <span
          style={{
            minWidth: "40px",
            textAlign: "right",
            fontWeight: 500,
            color: "#666",
          }}
        >
          {Math.round((1 - (currentStyle.fillOpacity ?? 1)) * 100)}%
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>枠線の透明度:</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={1 - (currentStyle.strokeOpacity ?? 1)}
          onChange={handleStrokeOpacity}
          onMouseUp={handleStrokeOpacityEnd}
          onTouchEnd={handleStrokeOpacityEnd}
          style={{
            flex: 1,
            height: "6px",
            background: "#ddd",
            borderRadius: "3px",
            outline: "none",
          }}
        />
        <span
          style={{
            minWidth: "40px",
            textAlign: "right",
            fontWeight: 500,
            color: "#666",
          }}
        >
          {Math.round((1 - (currentStyle.strokeOpacity ?? 1)) * 100)}%
        </span>
      </div>

      {/* 全体透明度（コメントアウト復旧） */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>全体透明度:</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={1 - currentStyle.opacity}
          onChange={handleOpacity}
          onMouseUp={handleOpacityEnd}
          onTouchEnd={handleOpacityEnd}
          style={{
            flex: 1,
            height: "6px",
            background: "#ddd",
            borderRadius: "3px",
            outline: "none",
          }}
        />
        <span
          style={{
            minWidth: "40px",
            textAlign: "right",
            fontWeight: 500,
            color: "#666",
          }}
        >
          {Math.round((1 - currentStyle.opacity) * 100)}%
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>回転角度:</label>
        <input
          type="range"
          min="0"
          max="360"
          step="1"
          value={currentStyle.angle || 0}
          onChange={handleAngle}
          onMouseUp={handleAngleEnd}
          onTouchEnd={handleAngleEnd}
          style={{
            flex: 1,
            height: "6px",
            background: "#ddd",
            borderRadius: "3px",
            outline: "none",
          }}
        />
        <span
          style={{
            minWidth: "40px",
            textAlign: "right",
            fontWeight: 500,
            color: "#666",
          }}
        >
          {Math.round(currentStyle.angle || 0)}°
        </span>
      </div>

      {/* 角の丸み（長方形のみ表示） */}
      {!isEllipse && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "15px",
            gap: "10px",
          }}
        >
          <label style={labelStyle}>角の丸み:</label>
          <input
            type="range"
            min="0"
            max="50"
            step="1"
            value={currentStyle.cornerRadius || 0}
            onChange={handleCornerRadius}
            onMouseUp={handleCornerRadiusEnd}
            onTouchEnd={handleCornerRadiusEnd}
            style={{
              flex: 1,
              height: "6px",
              background: "#ddd",
              borderRadius: "3px",
              outline: "none",
            }}
          />
          <span
            style={{
              minWidth: "40px",
              textAlign: "right",
              fontWeight: 500,
              color: "#666",
            }}
          >
            {Math.round(currentStyle.cornerRadius || 0)}px
          </span>
        </div>
      )}

      {/* テキスト設定 */}
      <h4
        style={{
          margin: "15px 0 10px 0",
          fontSize: "14px",
          color: "#333",
          borderBottom: "1px solid #eee",
          paddingBottom: "5px",
        }}
      >
        テキスト設定
      </h4>

      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "6px",
            fontWeight: 500,
            color: "#555",
          }}
        >
          テキスト:
        </label>
        <textarea
          value={displayText}
          placeholder="テキストを入力（複数行対応）"
          onChange={handleTextChange}
          onCompositionStart={handleTextCompositionStart}
          onCompositionEnd={handleTextCompositionEnd}
          style={{
            width: "100%",
            minHeight: "60px",
            padding: "6px 8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            fontSize: "13px",
            fontFamily: "inherit",
            resize: "vertical",
            boxSizing: "border-box",
          }}
          rows={3}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>文字色:</label>
        <input
          type="color"
          value={currentStyle.textColor || "#000000"}
          onChange={handleTextColor}
          style={{
            width: "40px",
            height: "30px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
            padding: 0,
          }}
        />
        <span
          style={{ fontFamily: "monospace", fontSize: "12px", color: "#666" }}
        >
          {currentStyle.textColor || "#000000"}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>フォントサイズ:</label>
        <input
          type="range"
          min="8"
          max="48"
          value={currentStyle.fontSize || 16}
          onChange={handleFontSize}
          onMouseUp={handleFontSizeEnd}
          onTouchEnd={handleFontSizeEnd}
          style={{
            flex: 1,
            height: "6px",
            background: "#ddd",
            borderRadius: "3px",
            outline: "none",
          }}
        />
        <span
          style={{
            minWidth: "40px",
            textAlign: "right",
            fontWeight: 500,
            color: "#666",
          }}
        >
          {Math.round(currentStyle.fontSize || 16)}px
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <label style={labelStyle}>フォントの太さ:</label>
        <input
          type="range"
          min="100"
          max="900"
          step="100"
          value={currentStyle.fontWeight || 400}
          onChange={handleFontWeight}
          onMouseUp={handleFontWeightEnd}
          onTouchEnd={handleFontWeightEnd}
          style={{
            flex: 1,
            height: "6px",
            background: "#ddd",
            borderRadius: "3px",
            outline: "none",
          }}
        />
        <span
          style={{
            minWidth: "40px",
            textAlign: "right",
            fontWeight: 500,
            color: "#666",
          }}
        >
          {currentStyle.fontWeight || 400}
        </span>
      </div>

      {/* 水平配置 */}
      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "6px",
            fontWeight: 500,
            color: "#555",
          }}
        >
          水平配置:
        </label>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => handleTextAlignH("left")}
            style={{
              ...buttonStyle,
              background:
                (currentStyle.textAlignH || "center") === "left"
                  ? "#007bff"
                  : "white",
              color:
                (currentStyle.textAlignH || "center") === "left"
                  ? "white"
                  : "#333",
            }}
          >
            左
          </button>
          <button
            onClick={() => handleTextAlignH("center")}
            style={{
              ...buttonStyle,
              background:
                (currentStyle.textAlignH || "center") === "center"
                  ? "#007bff"
                  : "white",
              color:
                (currentStyle.textAlignH || "center") === "center"
                  ? "white"
                  : "#333",
            }}
          >
            中央
          </button>
          <button
            onClick={() => handleTextAlignH("right")}
            style={{
              ...buttonStyle,
              background:
                (currentStyle.textAlignH || "center") === "right"
                  ? "#007bff"
                  : "white",
              color:
                (currentStyle.textAlignH || "center") === "right"
                  ? "white"
                  : "#333",
            }}
          >
            右
          </button>
        </div>
      </div>

      {/* 垂直配置 */}
      <div style={{ marginBottom: "15px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "6px",
            fontWeight: 500,
            color: "#555",
          }}
        >
          垂直配置:
        </label>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => handleTextAlignV("top")}
            style={{
              ...buttonStyle,
              background:
                (currentStyle.textAlignV || "middle") === "top"
                  ? "#007bff"
                  : "white",
              color:
                (currentStyle.textAlignV || "middle") === "top"
                  ? "white"
                  : "#333",
            }}
          >
            上
          </button>
          <button
            onClick={() => handleTextAlignV("middle")}
            style={{
              ...buttonStyle,
              background:
                (currentStyle.textAlignV || "middle") === "middle"
                  ? "#007bff"
                  : "white",
              color:
                (currentStyle.textAlignV || "middle") === "middle"
                  ? "white"
                  : "#333",
            }}
          >
            中央
          </button>
          <button
            onClick={() => handleTextAlignV("bottom")}
            style={{
              ...buttonStyle,
              background:
                (currentStyle.textAlignV || "middle") === "bottom"
                  ? "#007bff"
                  : "white",
              color:
                (currentStyle.textAlignV || "middle") === "bottom"
                  ? "white"
                  : "#333",
            }}
          >
            下
          </button>
        </div>
      </div>

      {/* 削除ボタン */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          gap: "10px",
        }}
      >
        <button
          onClick={onDeleteSelected}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #dc3545",
            borderRadius: "4px",
            background: "#f8d7da",
            color: "#721c24",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          🗑️ 選択した{shapeLabel}を削除
        </button>
      </div>
    </div>
  );
};

// DrawToolbarPortalコンポーネント（ReactのPortalでbody直下に配置）
const DrawToolbarPortal = ({
  isVisible,
  onStyleChange,
  onDeleteSelected,
  onSaveCanvas,
  currentStyle,
}) => {
  if (!isVisible) return null;

  return createPortal(
    <DrawToolbar
      isVisible={isVisible}
      onStyleChange={onStyleChange}
      onDeleteSelected={onDeleteSelected}
      onSaveCanvas={onSaveCanvas}
      currentStyle={currentStyle}
    />,
    document.body
  );
};

const DrawLayerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: visible;
  min-width: ${(props) => (props.isPrintMode ? "auto" : "1628px")};
  min-height: ${(props) => (props.isPrintMode ? "auto" : "1149px")};
  
  .draw-canvas {
    cursor: default;
    pointer-events: auto;
    
    &.drawing-mode {
      cursor: crosshair;
    }
  }
`;

export default DrawLayer;
